import * as client_reply from './alan_api';
import * as application from '../application/alan_api';
import * as client_request from '../client_request/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries:Map<string, () => T['node']>;

	constructor (entries:{[key:string]:T['init']}, protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, () => {
			const loaded_entry = this.initialize(this.parent, entry_key, entries[entry_key])
			this._entries.set(entry_key, () => loaded_entry);
			return loaded_entry;
		}]));
	}

	protected abstract initialize(parent: P, key: string, obj: T['init']): T['node'];
	protected abstract finalize(obj: T['node'], detach?: boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator](): IterableIterator<[string, T['node']]> {
		const iterator = this._entries.entries();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: [next.value[0], next.value[1]()]
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	entries(): IterableIterator<[string, T['node']]> {
		return this[Symbol.iterator]();
	}
	values(): IterableIterator<T['node']> {
		const iterator = this._entries.values();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: next.value()
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	keys(): IterableIterator<string> {
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		return Array.from(this.entries());
	}
	map<RT>(callback: (value: T['node']) => RT): Record<string, RT> {
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node'] | undefined {
		return this._entries.get(key)?.();
	}
	has(key: string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key: string, onExists: (($: T['node']) => RT) | Exclude<RT, Function>, onNotExists: (() => RT) | Exclude<RT, Function>): RT {
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry()) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cclient_reply
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cclient_reply|undefined;
	public abstract get root():Cclient_reply;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
export type Tsubscription_result_data = {
	'read permitted':'no'|['no', {}]|['yes', Tyes__read_permitted];
	'update permitted':Tpermission_data;
};
export class Csubscription_result_data extends AlanNode {
	public readonly properties:{
		readonly read_permitted:Csubscription_result_data.Dread_permitted,
		readonly update_permitted:Cpermission_data
	};
	constructor(init:Tsubscription_result_data, public location:AlanNode, public input: {
		node_subscription: () => client_request.Cnode_subscription
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			read_permitted: new Csubscription_result_data.Dread_permitted(init['read permitted'], $this),
			update_permitted: new Csubscription_result_data.Dupdate_permitted(init['update permitted'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__read_permitted = {
};
export class Cno__read_permitted extends AlanNode {
	constructor(init:Tno__read_permitted, public parent:Csubscription_result_data) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'read permitted'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__read_permitted = {
	'attributes':Record<string, Tattributes>;
};
export class Cyes__read_permitted extends AlanNode {
	public readonly properties:{
		readonly attributes:Cyes__read_permitted.Dattributes
	};
	constructor(init:Tyes__read_permitted, public parent:Csubscription_result_data) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cyes__read_permitted.Dattributes(init['attributes'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'read permitted'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kattributes extends Reference<client_request.Cattributes, string> {
	constructor(key:string, $this:Cattributes) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.node_subscription())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'::key`; }
}
export type Tattributes = {
	'type':['command', Tcommand]|['property', Tproperty];
};
export class Cattributes extends AlanDictionaryEntry {
	public key:Kattributes;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Cattributes.Dtype
	};
	constructor(key:string, init:Tattributes, public parent:Cyes__read_permitted) {
		super();
		const $this = this;
		this.key = new Kattributes(key, $this);
		this.properties = {
			type: new Cattributes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tcommand = {
	'execute permitted':Tpermission_data;
};
export class Ccommand extends AlanNode {
	public readonly properties:{
		readonly execute_permitted:Cpermission_data
	};
	public readonly inferences:{
		command: () => client_request.Ccommand
	} = {
		command: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'command') {
						return resolve(context.properties.type.state.node as client_request.Ccommand).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcommand, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			execute_permitted: new Ccommand.Dexecute_permitted(init['execute permitted'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'command'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty = {
	'type':['collection', Tcollection__type__property]|['file', Tfile__type__property]|['group', Tgroup__type__property]|['number', Tnumber__type__property]|['state group', Tstate_group__type__property]|['text', Ttext__type__property];
};
export class Cproperty extends AlanNode {
	public readonly properties:{
		readonly type:Cproperty.Dtype
	};
	public readonly inferences:{
		property: () => client_request.Cproperty__type__attributes
	} = {
		property: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as client_request.Cproperty__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tproperty, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			type: new Cproperty.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__property = {
	'create permitted':Tpermission_data;
	'graph endpoints result':'no'|['no', {}]|['yes', Tyes__graph_endpoints_result];
	'result':['entries', Tentries__result]|'update'|['update', {}];
};
export class Ccollection__type__property extends AlanNode {
	public readonly properties:{
		readonly create_permitted:Cpermission_data,
		readonly graph_endpoints_result:Ccollection__type__property.Dgraph_endpoints_result,
		readonly result:Ccollection__type__property.Dresult
	};
	public readonly inferences:{
		collection: () => client_request.Ccollection__type__property__type__attributes
	} = {
		collection: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as client_request.Ccollection__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			create_permitted: new Ccollection__type__property.Dcreate_permitted(init['create permitted'], $this),
			graph_endpoints_result: new Ccollection__type__property.Dgraph_endpoints_result(init['graph endpoints result'], $this),
			result: new Ccollection__type__property.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__graph_endpoints_result = {
};
export class Cno__graph_endpoints_result extends AlanNode {
	public readonly inferences:{
		no: () => client_request.Cno__include_graph_endpoints__collection__type__property
	} = {
		no: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__collection__graph_endpoints_result__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => {
					if (context?.properties.include_graph_endpoints.state.name === 'no') {
						return resolve(context.properties.include_graph_endpoints.state.node as client_request.Cno__include_graph_endpoints__collection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__graph_endpoints_result, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph endpoints result'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__graph_endpoints_result = {
	'result':['endpoints', Tendpoints__result__yes__graph_endpoints_result]|'none'|['none', {}];
};
export class Cyes__graph_endpoints_result extends AlanNode {
	public readonly properties:{
		readonly result:Cyes__graph_endpoints_result.Dresult
	};
	public readonly inferences:{
		yes: () => client_request.Cyes__include_graph_endpoints__collection__type__property
	} = {
		yes: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__collection__graph_endpoints_result__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => {
					if (context?.properties.include_graph_endpoints.state.name === 'yes') {
						return resolve(context.properties.include_graph_endpoints.state.node as client_request.Cyes__include_graph_endpoints__collection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__graph_endpoints_result, public parent:Ccollection__type__property) {
		super();
		const $this = this;
		this.properties = {
			result: new Cyes__graph_endpoints_result.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph endpoints result'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tendpoints__result__yes__graph_endpoints_result = {
	'graph endpoints':Record<string, Tgraph_endpoints__endpoints__result__yes__graph_endpoints_result>;
};
export class Cendpoints__result__yes__graph_endpoints_result extends AlanNode {
	public readonly properties:{
		readonly graph_endpoints:Cendpoints__result__yes__graph_endpoints_result.Dgraph_endpoints
	};
	constructor(init:Tendpoints__result__yes__graph_endpoints_result, public parent:Cyes__graph_endpoints_result) {
		super();
		const $this = this;
		this.properties = {
			graph_endpoints: new Cendpoints__result__yes__graph_endpoints_result.Dgraph_endpoints(init['graph endpoints'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'endpoints'`; }
	public get entity() { return this.parent.entity; }
}
export class Kgraph_endpoints__endpoints__result__yes__graph_endpoints_result extends Reference<application.Cordered, string> {
	constructor(key:string, $this:Cgraph_endpoints__endpoints__result__yes__graph_endpoints_result) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.collection())
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.graph_constraints)
			.then(context => {
				const entry = context?.properties.graphs.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'ordered') {
						return context.properties.type.state.node as application.Cordered;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph endpoints'::key`; }
}
export type Tgraph_endpoints__endpoints__result__yes__graph_endpoints_result = {
	'sink':string;
	'source':string;
};
export class Cgraph_endpoints__endpoints__result__yes__graph_endpoints_result extends AlanDictionaryEntry {
	public key:Kgraph_endpoints__endpoints__result__yes__graph_endpoints_result;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly sink:string,
		readonly source:string
	};
	constructor(key:string, init:Tgraph_endpoints__endpoints__result__yes__graph_endpoints_result, public parent:Cendpoints__result__yes__graph_endpoints_result) {
		super();
		const $this = this;
		this.key = new Kgraph_endpoints__endpoints__result__yes__graph_endpoints_result(key, $this);
		this.properties = {
			sink: init['sink'],
			source: init['source']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph endpoints'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tnone__result__yes__graph_endpoints_result = {
};
export class Cnone__result__yes__graph_endpoints_result extends AlanNode {
	constructor(init:Tnone__result__yes__graph_endpoints_result, public parent:Cyes__graph_endpoints_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__result = {
	'entries':Record<string, Tentries__entries__result>;
};
export class Centries__result extends AlanNode {
	public readonly properties:{
		readonly entries:Centries__result.Dentries
	};
	public readonly inferences:{
		subscribe_on_entries: () => client_request.Centries__subscribe_on
	} = {
		subscribe_on_entries: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__collection__result__entries_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => {
					if (context?.properties.subscribe_on.state.name === 'entries') {
						return resolve(context.properties.subscribe_on.state.node as client_request.Centries__subscribe_on).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tentries__result, public parent:Ccollection__type__property) {
		super();
		const $this = this;
		this.properties = {
			entries: new Centries__result.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'entries'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__entries__result = {
	'delete permitted':Tpermission_data;
	'node data':Tsubscription_result_data;
};
export class Centries__entries__result extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly delete_permitted:Cpermission_data,
		readonly node_data:Csubscription_result_data
	};
	constructor(key:string, init:Tentries__entries__result, public parent:Centries__result) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			delete_permitted: new Centries__entries__result.Ddelete_permitted(init['delete permitted'], $this),
			node_data: new Centries__entries__result.Dnode_data(init['node data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tupdate__result = {
};
export class Cupdate__result extends AlanNode {
	public readonly inferences:{
		update: () => client_request.Cupdates
	} = {
		update: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__collection__result__update_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => {
					if (context?.properties.subscribe_on.state.name === 'updates') {
						return resolve(context.properties.subscribe_on.state.node as client_request.Cupdates).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tupdate__result, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'update'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__property = {
	'extension':string;
	'token':string;
};
export class Cfile__type__property extends AlanNode {
	public readonly properties:{
		readonly extension:string,
		readonly token:string
	};
	public readonly inferences:{
		file: () => client_request.Cfile__type__property
	} = {
		file: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as client_request.Cfile__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			extension: init['extension'],
			token: init['token']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__property = {
	'node data':Tsubscription_result_data;
};
export class Cgroup__type__property extends AlanNode {
	public readonly properties:{
		readonly node_data:Csubscription_result_data
	};
	public readonly inferences:{
		group: () => client_request.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as client_request.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tgroup__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			node_data: new Cgroup__type__property.Dnode_data(init['node data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property = {
	'value':number;
};
export class Cnumber__type__property extends AlanNode {
	public readonly properties:{
		readonly value:Cnumber__type__property.Dvalue
	};
	public readonly inferences:{
		number: () => client_request.Cnumber__type__property__type__attributes
	} = {
		number: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as client_request.Cnumber__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			value: new Cnumber__type__property.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__property = {
	'create permitted':Record<string, {}>;
	'delete permitted':Tpermission_data;
	'node data':Tsubscription_result_data;
	'state':string;
};
export class Cstate_group__type__property extends AlanNode {
	public readonly properties:{
		readonly create_permitted:Cstate_group__type__property.Dcreate_permitted,
		readonly delete_permitted:Cpermission_data,
		readonly node_data:Csubscription_result_data,
		readonly state:Cstate_group__type__property.Dstate
	};
	public readonly inferences:{
		state_group: () => client_request.Cstate_group__type__property__type__attributes
	} = {
		state_group: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as client_request.Cstate_group__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			create_permitted: new Cstate_group__type__property.Dcreate_permitted(init['create permitted'], $this),
			delete_permitted: new Cstate_group__type__property.Ddelete_permitted(init['delete permitted'], $this),
			node_data: new Cstate_group__type__property.Dnode_data(init['node data'], $this),
			state: new Cstate_group__type__property.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export class Kcreate_permitted extends Reference<client_request.Cstates__state_group, string> {
	constructor(key:string, $this:Ccreate_permitted) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'create permitted'::key`; }
}
export type Tcreate_permitted = {
};
export class Ccreate_permitted extends AlanDictionaryEntry {
	public key:Kcreate_permitted;
	public get key_value() { return this.key.entry; }
	constructor(key:string, init:Tcreate_permitted, public parent:Cstate_group__type__property) {
		super();
		const $this = this;
		this.key = new Kcreate_permitted(key, $this);
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'create permitted'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Ttext__type__property = {
	'reference result':'no'|['no', {}]|['yes', Tyes__reference_result__text];
	'value':string;
};
export class Ctext__type__property extends AlanNode {
	public readonly properties:{
		readonly reference_result:Ctext__type__property.Dreference_result,
		readonly value:string
	};
	public readonly inferences:{
		text: () => client_request.Ctext__type__property__type__attributes
	} = {
		text: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as client_request.Ctext__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			reference_result: new Ctext__type__property.Dreference_result(init['reference result'], $this),
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__reference_result__text = {
};
export class Cno__reference_result__text extends AlanNode {
	public readonly inferences:{
		no: () => client_request.Cno__include_reference__text
	} = {
		no: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__text__reference_result__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.include_reference.state.name === 'no') {
						return resolve(context.properties.include_reference.state.node as client_request.Cno__include_reference__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__reference_result__text, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reference result'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__reference_result__text = {
	'result':'disconnected'|['disconnected', {}]|['node', Tnode__result__yes__reference_result]|'permission denied'|['permission denied', {}];
};
export class Cyes__reference_result__text extends AlanNode {
	public readonly properties:{
		readonly result:Cyes__reference_result__text.Dresult
	};
	public readonly inferences:{
		yes: () => client_request.Cyes__include_reference__text
	} = {
		yes: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__text__reference_result__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.include_reference.state.name === 'yes') {
						return resolve(context.properties.include_reference.state.node as client_request.Cyes__include_reference__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__reference_result__text, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			result: new Cyes__reference_result__text.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reference result'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdisconnected__result__yes__reference_result = {
};
export class Cdisconnected__result__yes__reference_result extends AlanNode {
	public readonly inferences:{
		optional_reference: () => application.Coptional__type,
		reference: () => application.Cyes__has_reference
	} = {
		optional_reference: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__text__reference_result__yes__result__disconnected_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.reference())
				.then(context => context?.properties.behaviour)
				.then(context => {
					if (context?.properties.type.state.name === 'optional') {
						return resolve(context.properties.type.state.node as application.Coptional__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		reference: cache((detach:boolean) => {
			const client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__text__reference_result__yes__result__disconnected_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.text())
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdisconnected__result__yes__reference_result, public parent:Cyes__reference_result__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'disconnected'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__result__yes__reference_result = {
	'id path':Tid_path__client_reply;
};
export class Cnode__result__yes__reference_result extends AlanNode {
	public readonly properties:{
		readonly id_path:Cid_path__client_reply
	};
	constructor(init:Tnode__result__yes__reference_result, public parent:Cyes__reference_result__text) {
		super();
		const $this = this;
		this.properties = {
			id_path: new Cnode__result__yes__reference_result.Did_path(init['id path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__result__yes__reference_result = {
};
export class Cpermission_denied__result__yes__reference_result extends AlanNode {
	constructor(init:Tpermission_denied__result__yes__reference_result, public parent:Cyes__reference_result__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_data = {
	'permitted':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cpermission_data extends AlanNode {
	public readonly properties:{
		readonly permitted:Cpermission_data.Dpermitted
	};
	constructor(init:Tpermission_data, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			permitted: new Cpermission_data.Dpermitted(init['permitted'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__permitted = {
};
export class Cno__permitted extends AlanNode {
	constructor(init:Tno__permitted, public parent:Cpermission_data) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'permitted'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__permitted = {
};
export class Cyes__permitted extends AlanNode {
	constructor(init:Tyes__permitted, public parent:Cpermission_data) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'permitted'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnotification_data = {
	'properties':Record<string, Tproperties__notification_data>;
};
export class Cnotification_data extends AlanNode {
	public readonly properties:{
		readonly properties:Cnotification_data.Dproperties
	};
	constructor(init:Tnotification_data, public location:AlanNode, public input: {
		node_subscription: () => client_request.Cnode_subscription
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cnotification_data.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kproperties__notification_data extends Reference<client_request.Cproperty__type__attributes, string> {
	constructor(key:string, $this:Cproperties__notification_data) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.node_subscription())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return context.properties.type.state.node as client_request.Cproperty__type__attributes;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__notification_data = {
	'type':['collection', Tcollection__type__properties]|['file', Tfile__type__properties]|['group', Tgroup__type__properties]|['number', Tnumber__type__properties]|['state group', Tstate_group__type__properties]|['text', Ttext__type__properties];
};
export class Cproperties__notification_data extends AlanDictionaryEntry {
	public key:Kproperties__notification_data;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Cproperties__notification_data.Dtype
	};
	constructor(key:string, init:Tproperties__notification_data, public parent:Cnotification_data) {
		super();
		const $this = this;
		this.key = new Kproperties__notification_data(key, $this);
		this.properties = {
			type: new Cproperties__notification_data.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tcollection__type__properties = {
	'graph endpoints update':'no'|['no', {}]|['yes', Tyes__graph_endpoints_update];
	'value update':['entries', Tentries__value_update]|'no'|['no', {}]|'updated'|['updated', {}];
};
export class Ccollection__type__properties extends AlanNode {
	public readonly properties:{
		readonly graph_endpoints_update:Ccollection__type__properties.Dgraph_endpoints_update,
		readonly value_update:Ccollection__type__properties.Dvalue_update
	};
	public readonly inferences:{
		collection: () => client_request.Ccollection__type__property__type__attributes
	} = {
		collection: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as client_request.Ccollection__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__properties, public parent:Cproperties__notification_data) {
		super();
		const $this = this;
		this.properties = {
			graph_endpoints_update: new Ccollection__type__properties.Dgraph_endpoints_update(init['graph endpoints update'], $this),
			value_update: new Ccollection__type__properties.Dvalue_update(init['value update'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__graph_endpoints_update = {
};
export class Cno__graph_endpoints_update extends AlanNode {
	constructor(init:Tno__graph_endpoints_update, public parent:Ccollection__type__properties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph endpoints update'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__graph_endpoints_update = {
	'result':['endpoints', Tendpoints__result__yes__graph_endpoints_update]|'none'|['none', {}];
};
export class Cyes__graph_endpoints_update extends AlanNode {
	public readonly properties:{
		readonly result:Cyes__graph_endpoints_update.Dresult
	};
	public readonly inferences:{
		yes: () => client_request.Cyes__include_graph_endpoints__collection__type__property
	} = {
		yes: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__collection__graph_endpoints_update__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => {
					if (context?.properties.include_graph_endpoints.state.name === 'yes') {
						return resolve(context.properties.include_graph_endpoints.state.node as client_request.Cyes__include_graph_endpoints__collection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__graph_endpoints_update, public parent:Ccollection__type__properties) {
		super();
		const $this = this;
		this.properties = {
			result: new Cyes__graph_endpoints_update.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph endpoints update'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tendpoints__result__yes__graph_endpoints_update = {
	'graph endpoints':Record<string, Tgraph_endpoints__endpoints__result__yes__graph_endpoints_update>;
};
export class Cendpoints__result__yes__graph_endpoints_update extends AlanNode {
	public readonly properties:{
		readonly graph_endpoints:Cendpoints__result__yes__graph_endpoints_update.Dgraph_endpoints
	};
	constructor(init:Tendpoints__result__yes__graph_endpoints_update, public parent:Cyes__graph_endpoints_update) {
		super();
		const $this = this;
		this.properties = {
			graph_endpoints: new Cendpoints__result__yes__graph_endpoints_update.Dgraph_endpoints(init['graph endpoints'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'endpoints'`; }
	public get entity() { return this.parent.entity; }
}
export class Kgraph_endpoints__endpoints__result__yes__graph_endpoints_update extends Reference<application.Cordered, string> {
	constructor(key:string, $this:Cgraph_endpoints__endpoints__result__yes__graph_endpoints_update) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.collection())
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.graph_constraints)
			.then(context => {
				const entry = context?.properties.graphs.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'ordered') {
						return context.properties.type.state.node as application.Cordered;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph endpoints'::key`; }
}
export type Tgraph_endpoints__endpoints__result__yes__graph_endpoints_update = {
	'sink':string;
	'source':string;
};
export class Cgraph_endpoints__endpoints__result__yes__graph_endpoints_update extends AlanDictionaryEntry {
	public key:Kgraph_endpoints__endpoints__result__yes__graph_endpoints_update;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly sink:string,
		readonly source:string
	};
	constructor(key:string, init:Tgraph_endpoints__endpoints__result__yes__graph_endpoints_update, public parent:Cendpoints__result__yes__graph_endpoints_update) {
		super();
		const $this = this;
		this.key = new Kgraph_endpoints__endpoints__result__yes__graph_endpoints_update(key, $this);
		this.properties = {
			sink: init['sink'],
			source: init['source']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph endpoints'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tnone__result__yes__graph_endpoints_update = {
};
export class Cnone__result__yes__graph_endpoints_update extends AlanNode {
	constructor(init:Tnone__result__yes__graph_endpoints_update, public parent:Cyes__graph_endpoints_update) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__value_update = {
	'entries':Record<string, Tentries__entries__value_update>;
};
export class Centries__value_update extends AlanNode {
	public readonly properties:{
		readonly entries:Centries__value_update.Dentries
	};
	public readonly inferences:{
		subscribe_on_entries: () => client_request.Centries__subscribe_on
	} = {
		subscribe_on_entries: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__collection__value_update__entries_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => {
					if (context?.properties.subscribe_on.state.name === 'entries') {
						return resolve(context.properties.subscribe_on.state.node as client_request.Centries__subscribe_on).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tentries__value_update, public parent:Ccollection__type__properties) {
		super();
		const $this = this;
		this.properties = {
			entries: new Centries__value_update.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value update'?'entries'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__entries__value_update = {
	'type':['create', Tcreate]|'remove'|['remove', {}]|['update', Tupdate__type__entries];
};
export class Centries__entries__value_update extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly type:Centries__entries__value_update.Dtype
	};
	constructor(key:string, init:Tentries__entries__value_update, public parent:Centries__value_update) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			type: new Centries__entries__value_update.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcreate = {
	'delete permitted':Tpermission_data;
	'node data':Tsubscription_result_data;
};
export class Ccreate extends AlanNode {
	public readonly properties:{
		readonly delete_permitted:Cpermission_data,
		readonly node_data:Csubscription_result_data
	};
	constructor(init:Tcreate, public parent:Centries__entries__value_update) {
		super();
		const $this = this;
		this.properties = {
			delete_permitted: new Ccreate.Ddelete_permitted(init['delete permitted'], $this),
			node_data: new Ccreate.Dnode_data(init['node data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'create'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremove = {
};
export class Cremove extends AlanNode {
	constructor(init:Tremove, public parent:Centries__entries__value_update) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'remove'`; }
	public get entity() { return this.parent.entity; }
}
export type Tupdate__type__entries = {
	'node data':Tnotification_data;
};
export class Cupdate__type__entries extends AlanNode {
	public readonly properties:{
		readonly node_data:Cnotification_data
	};
	constructor(init:Tupdate__type__entries, public parent:Centries__entries__value_update) {
		super();
		const $this = this;
		this.properties = {
			node_data: new Cupdate__type__entries.Dnode_data(init['node data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'update'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__value_update__collection = {
};
export class Cno__value_update__collection extends AlanNode {
	constructor(init:Tno__value_update__collection, public parent:Ccollection__type__properties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value update'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tupdated = {
};
export class Cupdated extends AlanNode {
	public readonly inferences:{
		updates: () => client_request.Cupdates
	} = {
		updates: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__collection__value_update__updated_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => {
					if (context?.properties.subscribe_on.state.name === 'updates') {
						return resolve(context.properties.subscribe_on.state.node as client_request.Cupdates).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tupdated, public parent:Ccollection__type__properties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value update'?'updated'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__properties = {
	'extension':string;
	'token':string;
};
export class Cfile__type__properties extends AlanNode {
	public readonly properties:{
		readonly extension:string,
		readonly token:string
	};
	public readonly inferences:{
		file: () => client_request.Cfile__type__property
	} = {
		file: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as client_request.Cfile__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__properties, public parent:Cproperties__notification_data) {
		super();
		const $this = this;
		this.properties = {
			extension: init['extension'],
			token: init['token']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__properties = {
	'notification data':Tnotification_data;
};
export class Cgroup__type__properties extends AlanNode {
	public readonly properties:{
		readonly notification_data:Cnotification_data
	};
	public readonly inferences:{
		group: () => client_request.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as client_request.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tgroup__type__properties, public parent:Cproperties__notification_data) {
		super();
		const $this = this;
		this.properties = {
			notification_data: new Cgroup__type__properties.Dnotification_data(init['notification data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__properties = {
	'value':number;
};
export class Cnumber__type__properties extends AlanNode {
	public readonly properties:{
		readonly value:Cnumber__type__properties.Dvalue
	};
	public readonly inferences:{
		number: () => client_request.Cnumber__type__property__type__attributes
	} = {
		number: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as client_request.Cnumber__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__properties, public parent:Cproperties__notification_data) {
		super();
		const $this = this;
		this.properties = {
			value: new Cnumber__type__properties.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__properties = {
	'state':string;
	'type':['set', Tset]|['update', Tupdate__type__state_group];
};
export class Cstate_group__type__properties extends AlanNode {
	public readonly properties:{
		readonly state:Cstate_group__type__properties.Dstate,
		readonly type:Cstate_group__type__properties.Dtype
	};
	public readonly inferences:{
		state_group: () => client_request.Cstate_group__type__property__type__attributes
	} = {
		state_group: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as client_request.Cstate_group__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__properties, public parent:Cproperties__notification_data) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate_group__type__properties.Dstate(init['state'], $this),
			type: new Cstate_group__type__properties.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset = {
	'delete permitted':Tpermission_data;
	'initialization data':Tsubscription_result_data;
};
export class Cset extends AlanNode {
	public readonly properties:{
		readonly delete_permitted:Cpermission_data,
		readonly initialization_data:Csubscription_result_data
	};
	constructor(init:Tset, public parent:Cstate_group__type__properties) {
		super();
		const $this = this;
		this.properties = {
			delete_permitted: new Cset.Ddelete_permitted(init['delete permitted'], $this),
			initialization_data: new Cset.Dinitialization_data(init['initialization data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'set'`; }
	public get entity() { return this.parent.entity; }
}
export type Tupdate__type__state_group = {
	'notification data':Tnotification_data;
};
export class Cupdate__type__state_group extends AlanNode {
	public readonly properties:{
		readonly notification_data:Cnotification_data
	};
	constructor(init:Tupdate__type__state_group, public parent:Cstate_group__type__properties) {
		super();
		const $this = this;
		this.properties = {
			notification_data: new Cupdate__type__state_group.Dnotification_data(init['notification data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'update'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__properties = {
	'reference update':'no'|['no', {}]|['yes', Tyes__reference_update];
	'value update':'no'|['no', {}]|['yes', Tyes__value_update];
};
export class Ctext__type__properties extends AlanNode {
	public readonly properties:{
		readonly reference_update:Ctext__type__properties.Dreference_update,
		readonly value_update:Ctext__type__properties.Dvalue_update
	};
	public readonly inferences:{
		text: () => client_request.Ctext__type__property__type__attributes
	} = {
		text: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as client_request.Ctext__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__properties, public parent:Cproperties__notification_data) {
		super();
		const $this = this;
		this.properties = {
			reference_update: new Ctext__type__properties.Dreference_update(init['reference update'], $this),
			value_update: new Ctext__type__properties.Dvalue_update(init['value update'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__reference_update = {
};
export class Cno__reference_update extends AlanNode {
	constructor(init:Tno__reference_update, public parent:Ctext__type__properties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reference update'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__reference_update = {
	'result':'disconnected'|['disconnected', {}]|['node', Tnode__result__yes__reference_update]|'permission denied'|['permission denied', {}];
};
export class Cyes__reference_update extends AlanNode {
	public readonly properties:{
		readonly result:Cyes__reference_update.Dresult
	};
	public readonly inferences:{
		yes: () => client_request.Cyes__include_reference__text
	} = {
		yes: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__text__reference_update__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.include_reference.state.name === 'yes') {
						return resolve(context.properties.include_reference.state.node as client_request.Cyes__include_reference__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__reference_update, public parent:Ctext__type__properties) {
		super();
		const $this = this;
		this.properties = {
			result: new Cyes__reference_update.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reference update'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdisconnected__result__yes__reference_update = {
};
export class Cdisconnected__result__yes__reference_update extends AlanNode {
	public readonly inferences:{
		optional_reference: () => application.Coptional__type,
		reference: () => application.Cyes__has_reference
	} = {
		optional_reference: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__text__reference_update__yes__result__disconnected_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.reference())
				.then(context => context?.properties.behaviour)
				.then(context => {
					if (context?.properties.type.state.name === 'optional') {
						return resolve(context.properties.type.state.node as application.Coptional__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		reference: cache((detach:boolean) => {
			const client_reply__notification_data__properties__type__text__reference_update__yes__result__disconnected_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.text())
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdisconnected__result__yes__reference_update, public parent:Cyes__reference_update) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'disconnected'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__result__yes__reference_update = {
	'id path':Tid_path__client_reply;
};
export class Cnode__result__yes__reference_update extends AlanNode {
	public readonly properties:{
		readonly id_path:Cid_path__client_reply
	};
	constructor(init:Tnode__result__yes__reference_update, public parent:Cyes__reference_update) {
		super();
		const $this = this;
		this.properties = {
			id_path: new Cnode__result__yes__reference_update.Did_path(init['id path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__result__yes__reference_update = {
};
export class Cpermission_denied__result__yes__reference_update extends AlanNode {
	constructor(init:Tpermission_denied__result__yes__reference_update, public parent:Cyes__reference_update) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__value_update__text = {
};
export class Cno__value_update__text extends AlanNode {
	constructor(init:Tno__value_update__text, public parent:Ctext__type__properties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value update'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__value_update = {
	'value':string;
};
export class Cyes__value_update extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	constructor(init:Tyes__value_update, public parent:Ctext__type__properties) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value update'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_query_result = {
	'properties':Record<string, Tproperties__node_query_result>;
};
export class Cnode_query_result extends AlanNode {
	public readonly properties:{
		readonly properties:Cnode_query_result.Dproperties
	};
	constructor(init:Tnode_query_result, public location:AlanNode, public input: {
		node_query: () => client_request.Cnode_query
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cnode_query_result.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kproperties__node_query_result extends Reference<client_request.Cproperties__node_query, string> {
	constructor(key:string, $this:Cproperties__node_query_result) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.node_query())
			.then(context => {
				const entry = context?.properties.properties.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__node_query_result = {
	'result':'does not exist'|['does not exist', {}]|['exists', Texists__result]|'permission denied'|['permission denied', {}];
};
export class Cproperties__node_query_result extends AlanDictionaryEntry {
	public key:Kproperties__node_query_result;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly result:Cproperties__node_query_result.Dresult
	};
	constructor(key:string, init:Tproperties__node_query_result, public parent:Cnode_query_result) {
		super();
		const $this = this;
		this.key = new Kproperties__node_query_result(key, $this);
		this.properties = {
			result: new Cproperties__node_query_result.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tdoes_not_exist__result = {
};
export class Cdoes_not_exist__result extends AlanNode {
	constructor(init:Tdoes_not_exist__result, public parent:Cproperties__node_query_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__result = {
	'type':['collection', Tcollection__type__exists]|['file', Tfile__type__exists]|['id path', Tid_path__type]|['number', Tnumber__type__exists]|['state group', Tstate_group__type__exists]|['text', Ttext__type__exists];
};
export class Cexists__result extends AlanNode {
	public readonly properties:{
		readonly type:Cexists__result.Dtype
	};
	constructor(init:Texists__result, public parent:Cproperties__node_query_result) {
		super();
		const $this = this;
		this.properties = {
			type: new Cexists__result.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__exists = {
	'entries':Record<string, Tentries__collection__type__exists>;
	'has graph endpoints':'no'|['no', {}]|['yes', Tyes__has_graph_endpoints];
};
export class Ccollection__type__exists extends AlanNode {
	public readonly properties:{
		readonly entries:Ccollection__type__exists.Dentries,
		readonly has_graph_endpoints:Ccollection__type__exists.Dhas_graph_endpoints
	};
	public readonly inferences:{
		collection: () => client_request.Ccollection__type__properties__node_query
	} = {
		collection: cache((detach:boolean) => {
			const client_reply__node_query_result__properties__result__exists__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as client_request.Ccollection__type__properties__node_query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__exists, public parent:Cexists__result) {
		super();
		const $this = this;
		this.properties = {
			entries: new Ccollection__type__exists.Dentries(init['entries'], $this),
			has_graph_endpoints: new Ccollection__type__exists.Dhas_graph_endpoints(init['has graph endpoints'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__collection__type__exists = {
	'properties':Tnode_query_result;
};
export class Centries__collection__type__exists extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly properties:Cnode_query_result
	};
	constructor(key:string, init:Tentries__collection__type__exists, public parent:Ccollection__type__exists) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			properties: new Centries__collection__type__exists.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_graph_endpoints = {
};
export class Cno__has_graph_endpoints extends AlanNode {
	constructor(init:Tno__has_graph_endpoints, public parent:Ccollection__type__exists) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has graph endpoints'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_graph_endpoints = {
	'ordered graphs':Record<string, Tordered_graphs>;
};
export class Cyes__has_graph_endpoints extends AlanNode {
	public readonly properties:{
		readonly ordered_graphs:Cyes__has_graph_endpoints.Dordered_graphs
	};
	public readonly inferences:{
		include_endpoints: () => client_request.Cyes__include_graph_endpoints__collection__type__properties
	} = {
		include_endpoints: cache((detach:boolean) => {
			const client_reply__node_query_result__properties__result__exists__type__collection__has_graph_endpoints__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => {
					if (context?.properties.include_graph_endpoints.state.name === 'yes') {
						return resolve(context.properties.include_graph_endpoints.state.node as client_request.Cyes__include_graph_endpoints__collection__type__properties).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_graph_endpoints, public parent:Ccollection__type__exists) {
		super();
		const $this = this;
		this.properties = {
			ordered_graphs: new Cyes__has_graph_endpoints.Dordered_graphs(init['ordered graphs'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has graph endpoints'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kordered_graphs extends Reference<application.Cordered, string> {
	constructor(key:string, $this:Cordered_graphs) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.graph_constraints)
			.then(context => {
				const entry = context?.properties.graphs.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'ordered') {
						return context.properties.type.state.node as application.Cordered;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'::key`; }
}
export type Tordered_graphs = {
	'sink':string;
	'source':string;
};
export class Cordered_graphs extends AlanDictionaryEntry {
	public key:Kordered_graphs;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly sink:string,
		readonly source:string
	};
	constructor(key:string, init:Tordered_graphs, public parent:Cyes__has_graph_endpoints) {
		super();
		const $this = this;
		this.key = new Kordered_graphs(key, $this);
		this.properties = {
			sink: init['sink'],
			source: init['source']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tfile__type__exists = {
	'extension':string;
	'token':string;
};
export class Cfile__type__exists extends AlanNode {
	public readonly properties:{
		readonly extension:string,
		readonly token:string
	};
	public readonly inferences:{
		file: () => client_request.Cfile__type__properties__node_query
	} = {
		file: cache((detach:boolean) => {
			const client_reply__node_query_result__properties__result__exists__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as client_request.Cfile__type__properties__node_query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__exists, public parent:Cexists__result) {
		super();
		const $this = this;
		this.properties = {
			extension: init['extension'],
			token: init['token']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tid_path__type = {
	'id path':Tid_path__client_reply;
};
export class Cid_path__type extends AlanNode {
	public readonly properties:{
		readonly id_path:Cid_path__client_reply
	};
	public readonly inferences:{
		id_path: () => client_request.Cid_path__type
	} = {
		id_path: cache((detach:boolean) => {
			const client_reply__node_query_result__properties__result__exists__type__id_path_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'id path') {
						return resolve(context.properties.type.state.node as client_request.Cid_path__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tid_path__type, public parent:Cexists__result) {
		super();
		const $this = this;
		this.properties = {
			id_path: new Cid_path__type.Did_path(init['id path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'id path'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__exists = {
	'value':number;
};
export class Cnumber__type__exists extends AlanNode {
	public readonly properties:{
		readonly value:Cnumber__type__exists.Dvalue
	};
	public readonly inferences:{
		number: () => client_request.Cnumber__type__properties__node_query
	} = {
		number: cache((detach:boolean) => {
			const client_reply__node_query_result__properties__result__exists__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as client_request.Cnumber__type__properties__node_query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__exists, public parent:Cexists__result) {
		super();
		const $this = this;
		this.properties = {
			value: new Cnumber__type__exists.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__exists = {
	'state':string;
};
export class Cstate_group__type__exists extends AlanNode {
	public readonly properties:{
		readonly state:Cstate_group__type__exists.Dstate
	};
	public readonly inferences:{
		state_group: () => client_request.Cstate_group__type__properties__node_query
	} = {
		state_group: cache((detach:boolean) => {
			const client_reply__node_query_result__properties__result__exists__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as client_request.Cstate_group__type__properties__node_query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__exists, public parent:Cexists__result) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate_group__type__exists.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__exists = {
	'value':string;
};
export class Ctext__type__exists extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	public readonly inferences:{
		text: () => client_request.Ctext__type__properties__node_query
	} = {
		text: cache((detach:boolean) => {
			const client_reply__node_query_result__properties__result__exists__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as client_request.Ctext__type__properties__node_query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__exists, public parent:Cexists__result) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__result__properties = {
};
export class Cpermission_denied__result__properties extends AlanNode {
	constructor(init:Tpermission_denied__result__properties, public parent:Cproperties__node_query_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist_query_result_entries = {
	'type':['collection', Tcollection__type__list_query_result_entries]|['node', Tnode__type]|'none'|['none', {}];
};
export class Clist_query_result_entries extends AlanNode {
	public readonly properties:{
		readonly type:Clist_query_result_entries.Dtype
	};
	constructor(init:Tlist_query_result_entries, public location:AlanNode, public input: {
		application: () => application.Capplication,
		collection_path: () => client_request.Ccollection_path,
		list_query: () => client_request.Cquery
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Clist_query_result_entries.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tcollection__type__list_query_result_entries = {
	'entries':Record<string, Tentries__collection__type__list_query_result_entries>;
};
export class Ccollection__type__list_query_result_entries extends AlanNode {
	public readonly properties:{
		readonly entries:Ccollection__type__list_query_result_entries.Dentries
	};
	public readonly inferences:{
		has_steps: () => client_request.Cyes__has_steps__collection_path
	} = {
		has_steps: cache((detach:boolean) => {
			const client_reply__list_query_result_entries__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.collection_path())
				.then(context => {
					if (context?.properties.has_steps.state.name === 'yes') {
						return resolve(context.properties.has_steps.state.node as client_request.Cyes__has_steps__collection_path).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__list_query_result_entries, public parent:Clist_query_result_entries) {
		super();
		const $this = this;
		this.properties = {
			entries: new Ccollection__type__list_query_result_entries.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__collection__type__list_query_result_entries = {
	'result':Tlist_query_result_entries;
};
export class Centries__collection__type__list_query_result_entries extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly result:Clist_query_result_entries
	};
	constructor(key:string, init:Tentries__collection__type__list_query_result_entries, public parent:Ccollection__type__list_query_result_entries) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			result: new Centries__collection__type__list_query_result_entries.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tnode__type = {
	'properties':Record<string, Tproperties__node>;
};
export class Cnode__type extends AlanNode {
	public readonly properties:{
		readonly properties:Cnode__type.Dproperties
	};
	public readonly inferences:{
		has_steps: () => client_request.Cno__has_steps__collection_path
	} = {
		has_steps: cache((detach:boolean) => {
			const client_reply__list_query_result_entries__type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.collection_path())
				.then(context => {
					if (context?.properties.has_steps.state.name === 'no') {
						return resolve(context.properties.has_steps.state.node as client_request.Cno__has_steps__collection_path).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnode__type, public parent:Clist_query_result_entries) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cnode__type.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export class Kproperties__node extends Reference<client_request.Cproperties__query, string> {
	constructor(key:string, $this:Cproperties__node) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.list_query())
			.then(context => {
				const entry = context?.properties.properties.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__node = {
	'is set':'no'|['no', {}]|['yes', Tyes__is_set];
};
export class Cproperties__node extends AlanDictionaryEntry {
	public key:Kproperties__node;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly is_set:Cproperties__node.Dis_set
	};
	constructor(key:string, init:Tproperties__node, public parent:Cnode__type) {
		super();
		const $this = this;
		this.key = new Kproperties__node(key, $this);
		this.properties = {
			is_set: new Cproperties__node.Dis_set(init['is set'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__is_set = {
};
export class Cno__is_set extends AlanNode {
	constructor(init:Tno__is_set, public parent:Cproperties__node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set = {
	'type':['number', Tnumber__type__yes]|['state group', Tstate_group__type__yes]|['text', Ttext__type__yes];
};
export class Cyes__is_set extends AlanNode {
	public readonly properties:{
		readonly type:Cyes__is_set.Dtype
	};
	constructor(init:Tyes__is_set, public parent:Cproperties__node) {
		super();
		const $this = this;
		this.properties = {
			type: new Cyes__is_set.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__yes = {
	'value':number;
};
export class Cnumber__type__yes extends AlanNode {
	public readonly properties:{
		readonly value:Cnumber__type__yes.Dvalue
	};
	public readonly inferences:{
		number: () => client_request.Cnumber__type__properties__query
	} = {
		number: cache((detach:boolean) => {
			const client_reply__list_query_result_entries__type__node__properties__is_set__yes__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as client_request.Cnumber__type__properties__query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__yes, public parent:Cyes__is_set) {
		super();
		const $this = this;
		this.properties = {
			value: new Cnumber__type__yes.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__yes = {
	'state':string;
};
export class Cstate_group__type__yes extends AlanNode {
	public readonly properties:{
		readonly state:Cstate_group__type__yes.Dstate
	};
	public readonly inferences:{
		state_group: () => client_request.Cstate_group__type__properties__query
	} = {
		state_group: cache((detach:boolean) => {
			const client_reply__list_query_result_entries__type__node__properties__is_set__yes__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as client_request.Cstate_group__type__properties__query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__yes, public parent:Cyes__is_set) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate_group__type__yes.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__yes = {
	'value':string;
};
export class Ctext__type__yes extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	public readonly inferences:{
		text: () => client_request.Ctext__type__properties__query
	} = {
		text: cache((detach:boolean) => {
			const client_reply__list_query_result_entries__type__node__properties__is_set__yes__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as client_request.Ctext__type__properties__query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__yes, public parent:Cyes__is_set) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__type = {
};
export class Cnone__type extends AlanNode {
	constructor(init:Tnone__type, public parent:Clist_query_result_entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tid_path__client_reply = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__id_path];
};
export class Cid_path__client_reply extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cid_path__client_reply.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tid_path__client_reply, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cid_path__client_reply.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__id_path = {
};
export class Cno__has_steps__id_path extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__id_path, public parent:Cid_path__client_reply) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__id_path = {
	'tail':Tid_path__client_reply;
	'type':['collection entry', Tcollection_entry]|['group', Tgroup__type__yes]|['state', Tstate];
};
export class Cyes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cid_path__client_reply,
		readonly type:Cyes__has_steps__id_path.Dtype
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__id_path, public parent:Cid_path__client_reply) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__id_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__id_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry = {
	'collection':string;
	'id':string;
};
export class Ccollection_entry extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection_entry.Dcollection,
		readonly id:string
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcollection_entry, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection_entry.Dcollection(init['collection'], $this),
			id: init['id']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes = {
	'group':string;
};
export class Cgroup__type__yes extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes.Dgroup
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate = {
	'state':string;
	'state group':string;
};
export class Cstate extends AlanNode {
	public readonly properties:{
		readonly state:Cstate.Dstate,
		readonly state_group:Cstate.Dstate_group
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate.Dstate(init['state'], $this),
			state_group: new Cstate.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgraph_node = {
	'children':Record<string, Tchildren>;
};
export class Cgraph_node extends AlanNode {
	public readonly properties:{
		readonly children:Cgraph_node.Dchildren
	};
	constructor(init:Tgraph_node, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			children: new Cgraph_node.Dchildren(init['children'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tchildren = {
	'graph':Tgraph_node;
	'link count':number;
};
export class Cchildren extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly graph:Cgraph_node,
		readonly link_count:Cchildren.Dlink_count
	};
	constructor(key:string, init:Tchildren, public parent:Cgraph_node) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			graph: new Cchildren.Dgraph(init['graph'], $this),
			link_count: new Cchildren.Dlink_count(init['link count'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'children'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tentry_points = {
	'type':['collection', Tcollection__type__entry_points]|['query', Tquery];
};
export class Centry_points extends AlanNode {
	public readonly properties:{
		readonly type:Centry_points.Dtype
	};
	constructor(init:Tentry_points, public location:AlanNode, public input: {
		application: () => application.Capplication,
		entry_path: () => client_request.Centry_point_path,
		list_query: () => client_request.Cquery
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Centry_points.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tcollection__type__entry_points = {
	'entries':Record<string, Tentries__collection__type__entry_points>;
};
export class Ccollection__type__entry_points extends AlanNode {
	public readonly properties:{
		readonly entries:Ccollection__type__entry_points.Dentries
	};
	public readonly inferences:{
		has_step: () => client_request.Cyes__has_steps__entry_point_path
	} = {
		has_step: cache((detach:boolean) => {
			const client_reply__entry_points__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.entry_path())
				.then(context => {
					if (context?.properties.has_steps.state.name === 'yes') {
						return resolve(context.properties.has_steps.state.node as client_request.Cyes__has_steps__entry_point_path).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__entry_points, public parent:Centry_points) {
		super();
		const $this = this;
		this.properties = {
			entries: new Ccollection__type__entry_points.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__collection__type__entry_points = {
	'entry points':Tentry_points;
};
export class Centries__collection__type__entry_points extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly entry_points:Centry_points
	};
	constructor(key:string, init:Tentries__collection__type__entry_points, public parent:Ccollection__type__entry_points) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			entry_points: new Centries__collection__type__entry_points.Dentry_points(init['entry points'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tquery = {
	'flat graph':Record<string, Tflat_graph>;
};
export class Cquery extends AlanNode {
	public readonly properties:{
		readonly flat_graph:Cquery.Dflat_graph
	};
	public readonly inferences:{
		no_step: () => client_request.Cno__has_steps__entry_point_path
	} = {
		no_step: cache((detach:boolean) => {
			const client_reply__entry_points__type__query_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.entry_path())
				.then(context => {
					if (context?.properties.has_steps.state.name === 'no') {
						return resolve(context.properties.has_steps.state.node as client_request.Cno__has_steps__entry_point_path).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tquery, public parent:Centry_points) {
		super();
		const $this = this;
		this.properties = {
			flat_graph: new Cquery.Dflat_graph(init['flat graph'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tflat_graph = {
	'count':number;
	'entries':Tlist_query_result_entries;
};
export class Cflat_graph extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly count:Cflat_graph.Dcount,
		readonly entries:Clist_query_result_entries
	};
	constructor(key:string, init:Tflat_graph, public parent:Cquery) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			count: new Cflat_graph.Dcount(init['count'], $this),
			entries: new Cflat_graph.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'flat graph'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcollection_query_entries = {
	'has steps':['no', Tno__has_steps__collection_query_entries]|['yes', Tyes__has_steps__collection_query_entries];
};
export class Ccollection_query_entries extends AlanNode {
	public readonly properties:{
		readonly has_steps:Ccollection_query_entries.Dhas_steps
	};
	constructor(init:Tcollection_query_entries, public location:AlanNode, public input: {
		collection_path: () => client_request.Ccollection_query_path,
		select_entries: () => client_request.Cyes__select_entries
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Ccollection_query_entries.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__collection_query_entries = {
	'properties':Tnode_query_result;
};
export class Cno__has_steps__collection_query_entries extends AlanNode {
	public readonly properties:{
		readonly properties:Cnode_query_result
	};
	public readonly inferences:{
		has_steps: () => client_request.Cno__has_steps__collection_query_path
	} = {
		has_steps: cache((detach:boolean) => {
			const client_reply__collection_query_entries__has_steps__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.collection_path())
				.then(context => {
					if (context?.properties.has_steps.state.name === 'no') {
						return resolve(context.properties.has_steps.state.node as client_request.Cno__has_steps__collection_query_path).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__has_steps__collection_query_entries, public parent:Ccollection_query_entries) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cno__has_steps__collection_query_entries.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__collection_query_entries = {
	'entries':Record<string, Tentries__yes>;
};
export class Cyes__has_steps__collection_query_entries extends AlanNode {
	public readonly properties:{
		readonly entries:Cyes__has_steps__collection_query_entries.Dentries
	};
	public readonly inferences:{
		step: () => client_request.Cyes__has_steps__collection_query_path
	} = {
		step: cache((detach:boolean) => {
			const client_reply__collection_query_entries__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.collection_path())
				.then(context => {
					if (context?.properties.has_steps.state.name === 'yes') {
						return resolve(context.properties.has_steps.state.node as client_request.Cyes__has_steps__collection_query_path).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_steps__collection_query_entries, public parent:Ccollection_query_entries) {
		super();
		const $this = this;
		this.properties = {
			entries: new Cyes__has_steps__collection_query_entries.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__yes = {
	'tail':Tcollection_query_entries;
	'type':['candidate', Tcandidate]|'existing entry'|['existing entry', {}];
};
export class Centries__yes extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly tail:Ccollection_query_entries,
		readonly type:Centries__yes.Dtype
	};
	constructor(key:string, init:Tentries__yes, public parent:Cyes__has_steps__collection_query_entries) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			tail: new Centries__yes.Dtail(init['tail'], $this),
			type: new Centries__yes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcandidate = {
	'reference result':'no'|['no', {}]|['yes', Tyes__reference_result__candidate];
};
export class Ccandidate extends AlanNode {
	public readonly properties:{
		readonly reference_result:Ccandidate.Dreference_result
	};
	public readonly inferences:{
		candidates: () => client_request.Ccandidates
	} = {
		candidates: cache((detach:boolean) => {
			const client_reply__collection_query_entries__has_steps__yes__entries__type__candidate_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.step())
				.then(context => {
					if (context?.properties.type.state.name === 'candidates') {
						return resolve(context.properties.type.state.node as client_request.Ccandidates).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcandidate, public parent:Centries__yes) {
		super();
		const $this = this;
		this.properties = {
			reference_result: new Ccandidate.Dreference_result(init['reference result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'candidate'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__reference_result__candidate = {
};
export class Cno__reference_result__candidate extends AlanNode {
	public readonly inferences:{
		no: () => client_request.Cno__include_reference__candidates
	} = {
		no: cache((detach:boolean) => {
			const client_reply__collection_query_entries__has_steps__yes__entries__type__candidate__reference_result__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.candidates())
				.then(context => {
					if (context?.properties.include_reference.state.name === 'no') {
						return resolve(context.properties.include_reference.state.node as client_request.Cno__include_reference__candidates).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__reference_result__candidate, public parent:Ccandidate) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reference result'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__reference_result__candidate = {
	'id path':Tid_path__client_reply;
};
export class Cyes__reference_result__candidate extends AlanNode {
	public readonly properties:{
		readonly id_path:Cid_path__client_reply
	};
	public readonly inferences:{
		yes: () => client_request.Cyes__include_reference__candidates
	} = {
		yes: cache((detach:boolean) => {
			const client_reply__collection_query_entries__has_steps__yes__entries__type__candidate__reference_result__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.candidates())
				.then(context => {
					if (context?.properties.include_reference.state.name === 'yes') {
						return resolve(context.properties.include_reference.state.node as client_request.Cyes__include_reference__candidates).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__reference_result__candidate, public parent:Ccandidate) {
		super();
		const $this = this;
		this.properties = {
			id_path: new Cyes__reference_result__candidate.Did_path(init['id path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reference result'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Texisting_entry = {
};
export class Cexisting_entry extends AlanNode {
	public readonly inferences:{
		existing_entries: () => client_request.Cexisting_entries
	} = {
		existing_entries: cache((detach:boolean) => {
			const client_reply__collection_query_entries__has_steps__yes__entries__type__existing_entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.step())
				.then(context => {
					if (context?.properties.type.state.name === 'existing entries') {
						return resolve(context.properties.type.state.node as client_request.Cexisting_entries).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texisting_entry, public parent:Centries__yes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'existing entry'`; }
	public get entity() { return this.parent.entity; }
}

export type Tclient_reply = {
	'type':['acyclic graph list query', Tacyclic_graph_list_query]|['acyclic graph tree query', Tacyclic_graph_tree_query]|['collection query', Tcollection_query]|'command execution'|['command execution', {}]|['mutation', Tmutation]|['notification', Tnotification]|['password mutation', Tpassword_mutation]|['password reset', Tpassword_reset]|['subscription', Tsubscription];
};
export class Cclient_reply extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public readonly properties:{
		readonly type:Cclient_reply.Dtype
	};
	constructor(init:Tclient_reply, public readonly input: {
	'application':application.Capplication
	'request':client_request.Cclient_request}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			type: new Cclient_reply.Dtype(init['type'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export type Tacyclic_graph_list_query = {
	'context':'does not exist'|['does not exist', {}]|['exists', Texists__context__acyclic_graph_list_query]|'permission denied'|['permission denied', {}];
};
export class Cacyclic_graph_list_query extends AlanNode {
	public readonly properties:{
		readonly context:Cacyclic_graph_list_query.Dcontext
	};
	public readonly inferences:{
		source: () => client_request.Cacyclic_graph_list_query
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__acyclic_graph_list_query_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'acyclic graph list query') {
						return resolve(context.properties.type.state.node as client_request.Cacyclic_graph_list_query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tacyclic_graph_list_query, public parent:Cclient_reply) {
		super();
		const $this = this;
		this.properties = {
			context: new Cacyclic_graph_list_query.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'acyclic graph list query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdoes_not_exist__context__acyclic_graph_list_query = {
};
export class Cdoes_not_exist__context__acyclic_graph_list_query extends AlanNode {
	constructor(init:Tdoes_not_exist__context__acyclic_graph_list_query, public parent:Cacyclic_graph_list_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__context__acyclic_graph_list_query = {
	'entry points':Tentry_points;
};
export class Cexists__context__acyclic_graph_list_query extends AlanNode {
	public readonly properties:{
		readonly entry_points:Centry_points
	};
	constructor(init:Texists__context__acyclic_graph_list_query, public parent:Cacyclic_graph_list_query) {
		super();
		const $this = this;
		this.properties = {
			entry_points: new Cexists__context__acyclic_graph_list_query.Dentry_points(init['entry points'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__context__acyclic_graph_list_query = {
};
export class Cpermission_denied__context__acyclic_graph_list_query extends AlanNode {
	constructor(init:Tpermission_denied__context__acyclic_graph_list_query, public parent:Cacyclic_graph_list_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tacyclic_graph_tree_query = {
	'context':'does not exist'|['does not exist', {}]|['exists', Texists__context__acyclic_graph_tree_query]|'permission denied'|['permission denied', {}];
};
export class Cacyclic_graph_tree_query extends AlanNode {
	public readonly properties:{
		readonly context:Cacyclic_graph_tree_query.Dcontext
	};
	public readonly inferences:{
		source: () => client_request.Cacyclic_graph_tree_query
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__acyclic_graph_tree_query_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'acyclic graph tree query') {
						return resolve(context.properties.type.state.node as client_request.Cacyclic_graph_tree_query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tacyclic_graph_tree_query, public parent:Cclient_reply) {
		super();
		const $this = this;
		this.properties = {
			context: new Cacyclic_graph_tree_query.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'acyclic graph tree query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdoes_not_exist__context__acyclic_graph_tree_query = {
};
export class Cdoes_not_exist__context__acyclic_graph_tree_query extends AlanNode {
	constructor(init:Tdoes_not_exist__context__acyclic_graph_tree_query, public parent:Cacyclic_graph_tree_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__context__acyclic_graph_tree_query = {
	'graph':Tgraph_node;
};
export class Cexists__context__acyclic_graph_tree_query extends AlanNode {
	public readonly properties:{
		readonly graph:Cgraph_node
	};
	constructor(init:Texists__context__acyclic_graph_tree_query, public parent:Cacyclic_graph_tree_query) {
		super();
		const $this = this;
		this.properties = {
			graph: new Cexists__context__acyclic_graph_tree_query.Dgraph(init['graph'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__context__acyclic_graph_tree_query = {
};
export class Cpermission_denied__context__acyclic_graph_tree_query extends AlanNode {
	constructor(init:Tpermission_denied__context__acyclic_graph_tree_query, public parent:Cacyclic_graph_tree_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_query = {
	'context':'does not exist'|['does not exist', {}]|['exists', Texists__context__collection_query]|'permission denied'|['permission denied', {}];
};
export class Ccollection_query extends AlanNode {
	public readonly properties:{
		readonly context:Ccollection_query.Dcontext
	};
	public readonly inferences:{
		source: () => client_request.Ccollection_query
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__collection_query_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'collection query') {
						return resolve(context.properties.type.state.node as client_request.Ccollection_query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection_query, public parent:Cclient_reply) {
		super();
		const $this = this;
		this.properties = {
			context: new Ccollection_query.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdoes_not_exist__context__collection_query = {
};
export class Cdoes_not_exist__context__collection_query extends AlanNode {
	constructor(init:Tdoes_not_exist__context__collection_query, public parent:Ccollection_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__context__collection_query = {
	'aggregates':Record<string, Taggregates>;
	'amount of entries':number;
	'entries':'no'|['no', {}]|['yes', Tyes__entries];
};
export class Cexists__context__collection_query extends AlanNode {
	public readonly properties:{
		readonly aggregates:Cexists__context__collection_query.Daggregates,
		readonly amount_of_entries:Cexists__context__collection_query.Damount_of_entries,
		readonly entries:Cexists__context__collection_query.Dentries
	};
	constructor(init:Texists__context__collection_query, public parent:Ccollection_query) {
		super();
		const $this = this;
		this.properties = {
			aggregates: new Cexists__context__collection_query.Daggregates(init['aggregates'], $this),
			amount_of_entries: new Cexists__context__collection_query.Damount_of_entries(init['amount of entries'], $this),
			entries: new Cexists__context__collection_query.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export class Kaggregates extends Reference<client_request.Caggregates, string> {
	constructor(key:string, $this:Caggregates) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.source())
			.then(context => {
				const entry = context?.properties.aggregates.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'aggregates'::key`; }
}
export type Taggregates = {
	'type':['state distribution', Tstate_distribution];
};
export class Caggregates extends AlanDictionaryEntry {
	public key:Kaggregates;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Caggregates.Dtype
	};
	constructor(key:string, init:Taggregates, public parent:Cexists__context__collection_query) {
		super();
		const $this = this;
		this.key = new Kaggregates(key, $this);
		this.properties = {
			type: new Caggregates.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'aggregates'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tstate_distribution = {
	'states':Record<string, Tstates>;
};
export class Cstate_distribution extends AlanNode {
	public readonly properties:{
		readonly states:Cstate_distribution.Dstates
	};
	public readonly inferences:{
		state_distribution_type: () => client_request.Cstate_distribution
	} = {
		state_distribution_type: cache((detach:boolean) => {
			const client_reply__type__collection_query__context__exists__aggregates__type__state_distribution_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state distribution') {
						return resolve(context.properties.type.state.node as client_request.Cstate_distribution).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_distribution, public parent:Caggregates) {
		super();
		const $this = this;
		this.properties = {
			states: new Cstate_distribution.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state distribution'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_distribution_type())
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates = {
	'amount':number;
};
export class Cstates extends AlanDictionaryEntry {
	public key:Kstates;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly amount:Cstates.Damount
	};
	constructor(key:string, init:Tstates, public parent:Cstate_distribution) {
		super();
		const $this = this;
		this.key = new Kstates(key, $this);
		this.properties = {
			amount: new Cstates.Damount(init['amount'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__entries = {
};
export class Cno__entries extends AlanNode {
	constructor(init:Tno__entries, public parent:Cexists__context__collection_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__entries = {
	'entries':Tcollection_query_entries;
	'limit exceeded':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__entries extends AlanNode {
	public readonly properties:{
		readonly entries:Ccollection_query_entries,
		readonly limit_exceeded:Cyes__entries.Dlimit_exceeded
	};
	public readonly inferences:{
		select_entries: () => client_request.Cyes__select_entries
	} = {
		select_entries: cache((detach:boolean) => {
			const client_reply__type__collection_query__context__exists__entries__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.source())
				.then(context => {
					if (context?.properties.select_entries.state.name === 'yes') {
						return resolve(context.properties.select_entries.state.node as client_request.Cyes__select_entries).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__entries, public parent:Cexists__context__collection_query) {
		super();
		const $this = this;
		this.properties = {
			entries: new Cyes__entries.Dentries(init['entries'], $this),
			limit_exceeded: new Cyes__entries.Dlimit_exceeded(init['limit exceeded'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__limit_exceeded = {
};
export class Cno__limit_exceeded extends AlanNode {
	constructor(init:Tno__limit_exceeded, public parent:Cyes__entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'limit exceeded'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__limit_exceeded = {
};
export class Cyes__limit_exceeded extends AlanNode {
	constructor(init:Tyes__limit_exceeded, public parent:Cyes__entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'limit exceeded'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__context__collection_query = {
};
export class Cpermission_denied__context__collection_query extends AlanNode {
	constructor(init:Tpermission_denied__context__collection_query, public parent:Ccollection_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcommand_execution = {
};
export class Ccommand_execution extends AlanNode {
	public readonly inferences:{
		source: () => client_request.Ccommand_execution
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__command_execution_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'command execution') {
						return resolve(context.properties.type.state.node as client_request.Ccommand_execution).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcommand_execution, public parent:Cclient_reply) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'command execution'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmutation = {
	'context':'does not exist'|['does not exist', {}]|['exists', Texists__context__mutation]|'permission denied'|['permission denied', {}];
};
export class Cmutation extends AlanNode {
	public readonly properties:{
		readonly context:Cmutation.Dcontext
	};
	public readonly inferences:{
		source: () => client_request.Cmutation
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__mutation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'mutation') {
						return resolve(context.properties.type.state.node as client_request.Cmutation).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tmutation, public parent:Cclient_reply) {
		super();
		const $this = this;
		this.properties = {
			context: new Cmutation.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'mutation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdoes_not_exist__context__mutation = {
};
export class Cdoes_not_exist__context__mutation extends AlanNode {
	constructor(init:Tdoes_not_exist__context__mutation, public parent:Cmutation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__context__mutation = {
	'result':'failure'|['failure', {}]|'success'|['success', {}];
};
export class Cexists__context__mutation extends AlanNode {
	public readonly properties:{
		readonly result:Cexists__context__mutation.Dresult
	};
	constructor(init:Texists__context__mutation, public parent:Cmutation) {
		super();
		const $this = this;
		this.properties = {
			result: new Cexists__context__mutation.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfailure__result__exists__context = {
};
export class Cfailure__result__exists__context extends AlanNode {
	constructor(init:Tfailure__result__exists__context, public parent:Cexists__context__mutation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'failure'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__result__exists__context = {
};
export class Csuccess__result__exists__context extends AlanNode {
	constructor(init:Tsuccess__result__exists__context, public parent:Cexists__context__mutation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__context__mutation = {
};
export class Cpermission_denied__context__mutation extends AlanNode {
	constructor(init:Tpermission_denied__context__mutation, public parent:Cmutation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnotification = {
	'context':'does not exist'|['does not exist', {}]|['exists', Texists__context__notification]|'permission denied'|['permission denied', {}];
};
export class Cnotification extends AlanNode {
	public readonly properties:{
		readonly context:Cnotification.Dcontext
	};
	public readonly inferences:{
		source: () => client_request.Csubscription
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__notification_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'subscription') {
						return resolve(context.properties.type.state.node as client_request.Csubscription).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnotification, public parent:Cclient_reply) {
		super();
		const $this = this;
		this.properties = {
			context: new Cnotification.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'notification'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdoes_not_exist__context__notification = {
};
export class Cdoes_not_exist__context__notification extends AlanNode {
	constructor(init:Tdoes_not_exist__context__notification, public parent:Cnotification) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__context__notification = {
	'notification data':Tnotification_data;
};
export class Cexists__context__notification extends AlanNode {
	public readonly properties:{
		readonly notification_data:Cnotification_data
	};
	constructor(init:Texists__context__notification, public parent:Cnotification) {
		super();
		const $this = this;
		this.properties = {
			notification_data: new Cexists__context__notification.Dnotification_data(init['notification data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__context__notification = {
};
export class Cpermission_denied__context__notification extends AlanNode {
	constructor(init:Tpermission_denied__context__notification, public parent:Cnotification) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpassword_mutation = {
	'user context':'does not exist'|['does not exist', {}]|['exists', Texists__user_context__password_mutation]|'permission denied'|['permission denied', {}];
};
export class Cpassword_mutation extends AlanNode {
	public readonly properties:{
		readonly user_context:Cpassword_mutation.Duser_context
	};
	public readonly inferences:{
		source: () => client_request.Cpassword_mutation
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__password_mutation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'password mutation') {
						return resolve(context.properties.type.state.node as client_request.Cpassword_mutation).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tpassword_mutation, public parent:Cclient_reply) {
		super();
		const $this = this;
		this.properties = {
			user_context: new Cpassword_mutation.Duser_context(init['user context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'password mutation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdoes_not_exist__user_context__password_mutation = {
};
export class Cdoes_not_exist__user_context__password_mutation extends AlanNode {
	constructor(init:Tdoes_not_exist__user_context__password_mutation, public parent:Cpassword_mutation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'user context'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__user_context__password_mutation = {
	'result':'failure'|['failure', {}]|'success'|['success', {}];
};
export class Cexists__user_context__password_mutation extends AlanNode {
	public readonly properties:{
		readonly result:Cexists__user_context__password_mutation.Dresult
	};
	constructor(init:Texists__user_context__password_mutation, public parent:Cpassword_mutation) {
		super();
		const $this = this;
		this.properties = {
			result: new Cexists__user_context__password_mutation.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'user context'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfailure__result__exists__user_context__password_mutation = {
};
export class Cfailure__result__exists__user_context__password_mutation extends AlanNode {
	constructor(init:Tfailure__result__exists__user_context__password_mutation, public parent:Cexists__user_context__password_mutation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'failure'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__result__exists__user_context__password_mutation = {
};
export class Csuccess__result__exists__user_context__password_mutation extends AlanNode {
	constructor(init:Tsuccess__result__exists__user_context__password_mutation, public parent:Cexists__user_context__password_mutation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__user_context__password_mutation = {
};
export class Cpermission_denied__user_context__password_mutation extends AlanNode {
	constructor(init:Tpermission_denied__user_context__password_mutation, public parent:Cpassword_mutation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'user context'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpassword_reset = {
	'user context':'does not exist'|['does not exist', {}]|['exists', Texists__user_context__password_reset]|'permission denied'|['permission denied', {}];
};
export class Cpassword_reset extends AlanNode {
	public readonly properties:{
		readonly user_context:Cpassword_reset.Duser_context
	};
	public readonly inferences:{
		source: () => client_request.Cpassword_reset
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__password_reset_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'password reset') {
						return resolve(context.properties.type.state.node as client_request.Cpassword_reset).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tpassword_reset, public parent:Cclient_reply) {
		super();
		const $this = this;
		this.properties = {
			user_context: new Cpassword_reset.Duser_context(init['user context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'password reset'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdoes_not_exist__user_context__password_reset = {
};
export class Cdoes_not_exist__user_context__password_reset extends AlanNode {
	constructor(init:Tdoes_not_exist__user_context__password_reset, public parent:Cpassword_reset) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'user context'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__user_context__password_reset = {
	'result':'failure'|['failure', {}]|['success', Tsuccess__result__exists__user_context__password_reset];
};
export class Cexists__user_context__password_reset extends AlanNode {
	public readonly properties:{
		readonly result:Cexists__user_context__password_reset.Dresult
	};
	constructor(init:Texists__user_context__password_reset, public parent:Cpassword_reset) {
		super();
		const $this = this;
		this.properties = {
			result: new Cexists__user_context__password_reset.Dresult(init['result'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'user context'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfailure__result__exists__user_context__password_reset = {
};
export class Cfailure__result__exists__user_context__password_reset extends AlanNode {
	constructor(init:Tfailure__result__exists__user_context__password_reset, public parent:Cexists__user_context__password_reset) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'failure'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__result__exists__user_context__password_reset = {
	'temporary password':string;
};
export class Csuccess__result__exists__user_context__password_reset extends AlanNode {
	public readonly properties:{
		readonly temporary_password:string
	};
	constructor(init:Tsuccess__result__exists__user_context__password_reset, public parent:Cexists__user_context__password_reset) {
		super();
		const $this = this;
		this.properties = {
			temporary_password: init['temporary password']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__user_context__password_reset = {
};
export class Cpermission_denied__user_context__password_reset extends AlanNode {
	constructor(init:Tpermission_denied__user_context__password_reset, public parent:Cpassword_reset) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'user context'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsubscription = {
	'context':'does not exist'|['does not exist', {}]|['exists', Texists__context__subscription]|'permission denied'|['permission denied', {}];
};
export class Csubscription extends AlanNode {
	public readonly properties:{
		readonly context:Csubscription.Dcontext
	};
	public readonly inferences:{
		source: () => client_request.Csubscription
	} = {
		source: cache((detach:boolean) => {
			const client_reply__type__subscription_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.request)
				.then(context => {
					if (context?.properties.type.state.name === 'subscription') {
						return resolve(context.properties.type.state.node as client_request.Csubscription).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tsubscription, public parent:Cclient_reply) {
		super();
		const $this = this;
		this.properties = {
			context: new Csubscription.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'subscription'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdoes_not_exist__context__subscription = {
};
export class Cdoes_not_exist__context__subscription extends AlanNode {
	constructor(init:Tdoes_not_exist__context__subscription, public parent:Csubscription) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'does not exist'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists__context__subscription = {
	'delete permitted':Tpermission_data;
	'subscription result data':Tsubscription_result_data;
};
export class Cexists__context__subscription extends AlanNode {
	public readonly properties:{
		readonly delete_permitted:Cpermission_data,
		readonly subscription_result_data:Csubscription_result_data
	};
	constructor(init:Texists__context__subscription, public parent:Csubscription) {
		super();
		const $this = this;
		this.properties = {
			delete_permitted: new Cexists__context__subscription.Ddelete_permitted(init['delete permitted'], $this),
			subscription_result_data: new Cexists__context__subscription.Dsubscription_result_data(init['subscription result data'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission_denied__context__subscription = {
};
export class Cpermission_denied__context__subscription extends AlanNode {
	constructor(init:Tpermission_denied__context__subscription, public parent:Csubscription) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'permission denied'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Ccollection_query_entries {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__collection_query_entries, init:Tno__has_steps__collection_query_entries}|
		{ name: 'yes', node:Cyes__has_steps__collection_query_entries, init:Tyes__has_steps__collection_query_entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__collection_query_entries, parent:Ccollection_query_entries) => new Cno__has_steps__collection_query_entries(init, parent);
				case 'yes': return (init:Tyes__has_steps__collection_query_entries, parent:Ccollection_query_entries) => new Cyes__has_steps__collection_query_entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__collection_query_entries;
				case 'yes': return finalize_yes__has_steps__collection_query_entries;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_query_entries['has steps'], parent:Ccollection_query_entries) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cno__has_steps__collection_query_entries {
	export class Dproperties extends Cnode_query_result {
		constructor(data:Tno__has_steps__collection_query_entries['properties'], parent:Cno__has_steps__collection_query_entries) {
			super(data, parent, {
				node_query: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.select_entries())
					.then(context => context?.properties.properties).result!)
			}, 'properties')
		}
	}
}
export namespace Cyes__has_steps__collection_query_entries {
	export class Dentries extends AlanDictionary<{ node:Centries__yes, init:Tentries__yes},Cyes__has_steps__collection_query_entries> {
		protected initialize(parent:Cyes__has_steps__collection_query_entries, key:string, entry_init:Tentries__yes) { return new Centries__yes(key, entry_init, parent); }
		protected finalize = finalize_entries__yes
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tyes__has_steps__collection_query_entries['entries'], parent:Cyes__has_steps__collection_query_entries) {
			super(data, parent);
		}
	}
}
export namespace Centries__yes {
	export class Dtail extends Ccollection_query_entries {
		constructor(data:Tentries__yes['tail'], parent:Centries__yes) {
			super(data, parent, {
				collection_path: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.step())
					.then(context => context?.properties.tail).result!),
				select_entries: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.select_entries()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'candidate', node:Ccandidate, init:Tcandidate}|
		{ name: 'existing entry', node:Cexisting_entry, init:Texisting_entry}> {
		protected initializer(state: 'candidate'|'existing entry') {
			switch (state) {
				case 'candidate': return (init:Tcandidate, parent:Centries__yes) => new Ccandidate(init, parent);
				case 'existing entry': return (init:Texisting_entry, parent:Centries__yes) => new Cexisting_entry(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'candidate'|'existing entry') {
			switch (state) {
				case 'candidate': return finalize_candidate;
				case 'existing entry': return finalize_existing_entry;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__yes['type'], parent:Centries__yes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccandidate {
	export class Dreference_result extends StateGroup<
		{ name: 'no', node:Cno__reference_result__candidate, init:Tno__reference_result__candidate}|
		{ name: 'yes', node:Cyes__reference_result__candidate, init:Tyes__reference_result__candidate}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__reference_result__candidate, parent:Ccandidate) => new Cno__reference_result__candidate(init, parent);
				case 'yes': return (init:Tyes__reference_result__candidate, parent:Ccandidate) => new Cyes__reference_result__candidate(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__reference_result__candidate;
				case 'yes': return finalize_yes__reference_result__candidate;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcandidate['reference result'], parent:Ccandidate) {
			super(data, parent, 'reference result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'reference result'`; }
	}
}
export namespace Cyes__reference_result__candidate {
	export class Did_path extends Cid_path__client_reply {
		constructor(data:Tyes__reference_result__candidate['id path'], parent:Cyes__reference_result__candidate) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'id path')
		}
	}
}
export namespace Centry_points {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__entry_points, init:Tcollection__type__entry_points}|
		{ name: 'query', node:Cquery, init:Tquery}> {
		protected initializer(state: 'collection'|'query') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__entry_points, parent:Centry_points) => new Ccollection__type__entry_points(init, parent);
				case 'query': return (init:Tquery, parent:Centry_points) => new Cquery(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'query') {
			switch (state) {
				case 'collection': return finalize_collection__type__entry_points;
				case 'query': return finalize_query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry_points['type'], parent:Centry_points) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__entry_points {
	export class Dentries extends AlanDictionary<{ node:Centries__collection__type__entry_points, init:Tentries__collection__type__entry_points},Ccollection__type__entry_points> {
		protected initialize(parent:Ccollection__type__entry_points, key:string, entry_init:Tentries__collection__type__entry_points) { return new Centries__collection__type__entry_points(key, entry_init, parent); }
		protected finalize = finalize_entries__collection__type__entry_points
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tcollection__type__entry_points['entries'], parent:Ccollection__type__entry_points) {
			super(data, parent);
		}
	}
}
export namespace Centries__collection__type__entry_points {
	export class Dentry_points extends Centry_points {
		constructor(data:Tentries__collection__type__entry_points['entry points'], parent:Centries__collection__type__entry_points) {
			super(data, parent, {
				application: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.application()).result!),
				entry_path: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.has_step())
					.then(context => context?.properties.tail).result!),
				list_query: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.list_query()).result!)
			}, 'entry points')
		}
	}
}
export namespace Cquery {
	export class Dflat_graph extends AlanDictionary<{ node:Cflat_graph, init:Tflat_graph},Cquery> {
		protected initialize(parent:Cquery, key:string, entry_init:Tflat_graph) { return new Cflat_graph(key, entry_init, parent); }
		protected finalize = finalize_flat_graph
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'flat graph'`; }
		constructor(data:Tquery['flat graph'], parent:Cquery) {
			super(data, parent);
		}
	}
}
export namespace Cflat_graph {
	export class Dcount extends AlanInteger {
		constructor(data:Tflat_graph['count'], parent:Cflat_graph) {
			number__is_non_negative(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'count'`; }
	}
	export class Dentries extends Clist_query_result_entries {
		constructor(data:Tflat_graph['entries'], parent:Cflat_graph) {
			super(data, parent, {
				application: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application).result!),
				collection_path: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.list_query())
					.then(context => context?.properties.collection_path).result!),
				list_query: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.list_query()).result!)
			}, 'entries')
		}
	}
}
export namespace Cgraph_node {
	export class Dchildren extends AlanDictionary<{ node:Cchildren, init:Tchildren},Cgraph_node> {
		protected initialize(parent:Cgraph_node, key:string, entry_init:Tchildren) { return new Cchildren(key, entry_init, parent); }
		protected finalize = finalize_children
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'children'`; }
		constructor(data:Tgraph_node['children'], parent:Cgraph_node) {
			super(data, parent);
		}
	}
}
export namespace Cchildren {
	export class Dgraph extends Cgraph_node {
		constructor(data:Tchildren['graph'], parent:Cchildren) {
			super(data, parent, 'graph')
		}
	}
	export class Dlink_count extends AlanInteger {
		constructor(data:Tchildren['link count'], parent:Cchildren) {
			number__is_non_negative(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'link count'`; }
	}
}
export namespace Cid_path__client_reply {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__id_path, init:Tno__has_steps__id_path}|
		{ name: 'yes', node:Cyes__has_steps__id_path, init:Tyes__has_steps__id_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__id_path, parent:Cid_path__client_reply) => new Cno__has_steps__id_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__id_path, parent:Cid_path__client_reply) => new Cyes__has_steps__id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__id_path;
				case 'yes': return finalize_yes__has_steps__id_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tid_path__client_reply['has steps'], parent:Cid_path__client_reply) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__id_path {
	export class Dtail extends Cid_path__client_reply {
		constructor(data:Tyes__has_steps__id_path['tail'], parent:Cyes__has_steps__id_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection entry', node:Ccollection_entry, init:Tcollection_entry}|
		{ name: 'group', node:Cgroup__type__yes, init:Tgroup__type__yes}|
		{ name: 'state', node:Cstate, init:Tstate}> {
		protected initializer(state: 'collection entry'|'group'|'state') {
			switch (state) {
				case 'collection entry': return (init:Tcollection_entry, parent:Cyes__has_steps__id_path) => new Ccollection_entry(init, parent);
				case 'group': return (init:Tgroup__type__yes, parent:Cyes__has_steps__id_path) => new Cgroup__type__yes(init, parent);
				case 'state': return (init:Tstate, parent:Cyes__has_steps__id_path) => new Cstate(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection entry'|'group'|'state') {
			switch (state) {
				case 'collection entry': return finalize_collection_entry;
				case 'group': return finalize_group__type__yes;
				case 'state': return finalize_state;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__id_path['type'], parent:Cyes__has_steps__id_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection_entry {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection_entry) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cgroup__type__yes {
	export class Dgroup extends Reference<application.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as application.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cstate {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Clist_query_result_entries {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__list_query_result_entries, init:Tcollection__type__list_query_result_entries}|
		{ name: 'node', node:Cnode__type, init:Tnode__type}|
		{ name: 'none', node:Cnone__type, init:Tnone__type}> {
		protected initializer(state: 'collection'|'node'|'none') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__list_query_result_entries, parent:Clist_query_result_entries) => new Ccollection__type__list_query_result_entries(init, parent);
				case 'node': return (init:Tnode__type, parent:Clist_query_result_entries) => new Cnode__type(init, parent);
				case 'none': return (init:Tnone__type, parent:Clist_query_result_entries) => new Cnone__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'node'|'none') {
			switch (state) {
				case 'collection': return finalize_collection__type__list_query_result_entries;
				case 'node': return finalize_node__type;
				case 'none': return finalize_none__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tlist_query_result_entries['type'], parent:Clist_query_result_entries) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__list_query_result_entries {
	export class Dentries extends AlanDictionary<{ node:Centries__collection__type__list_query_result_entries, init:Tentries__collection__type__list_query_result_entries},Ccollection__type__list_query_result_entries> {
		protected initialize(parent:Ccollection__type__list_query_result_entries, key:string, entry_init:Tentries__collection__type__list_query_result_entries) { return new Centries__collection__type__list_query_result_entries(key, entry_init, parent); }
		protected finalize = finalize_entries__collection__type__list_query_result_entries
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tcollection__type__list_query_result_entries['entries'], parent:Ccollection__type__list_query_result_entries) {
			super(data, parent);
		}
	}
}
export namespace Centries__collection__type__list_query_result_entries {
	export class Dresult extends Clist_query_result_entries {
		constructor(data:Tentries__collection__type__list_query_result_entries['result'], parent:Centries__collection__type__list_query_result_entries) {
			super(data, parent, {
				application: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.application()).result!),
				collection_path: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.has_steps())
					.then(context => context?.properties.tail).result!),
				list_query: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.list_query()).result!)
			}, 'result')
		}
	}
}
export namespace Cnode__type {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__node, init:Tproperties__node},Cnode__type> {
		protected initialize(parent:Cnode__type, key:string, entry_init:Tproperties__node) { return new Cproperties__node(key, entry_init, parent); }
		protected finalize = finalize_properties__node
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_reply__list_query_result_entries__type__node__properties_key_nval:client_request.Cproperties__query):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.list_query())
			.then(context => {
				for (let [key,val] of context?.properties.properties) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tnode__type['properties'], parent:Cnode__type) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__node {
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set, init:Tno__is_set}|
		{ name: 'yes', node:Cyes__is_set, init:Tyes__is_set}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set, parent:Cproperties__node) => new Cno__is_set(init, parent);
				case 'yes': return (init:Tyes__is_set, parent:Cproperties__node) => new Cyes__is_set(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__is_set;
				case 'yes': return finalize_yes__is_set;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__node['is set'], parent:Cproperties__node) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
}
export namespace Cyes__is_set {
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__yes, init:Tnumber__type__yes}|
		{ name: 'state group', node:Cstate_group__type__yes, init:Tstate_group__type__yes}|
		{ name: 'text', node:Ctext__type__yes, init:Ttext__type__yes}> {
		protected initializer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__yes, parent:Cyes__is_set) => new Cnumber__type__yes(init, parent);
				case 'state group': return (init:Tstate_group__type__yes, parent:Cyes__is_set) => new Cstate_group__type__yes(init, parent);
				case 'text': return (init:Ttext__type__yes, parent:Cyes__is_set) => new Ctext__type__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__yes;
				case 'state group': return finalize_state_group__type__yes;
				case 'text': return finalize_text__type__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_set['type'], parent:Cyes__is_set) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumber__type__yes {
	export class Dvalue extends AlanInteger {
		public readonly inferences:{
			set_type: () => application.Cnumber_set_type
		}
		constructor(data:Tnumber__type__yes['value'], parent:Cnumber__type__yes) {
			super(data, parent);this.inferences = {
				set_type: cache((detach:boolean) => {
					const client_reply__list_query_result_entries__type__node__properties__is_set__yes__type__number__value_nval = this;
					return resolve(this)
						.then(switch_context => { 
							const value = resolve(switch_context)
								.then(context => client_reply__list_query_result_entries__type__node__properties__is_set__yes__type__number__value_nval).result.value;
							if (value < 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.inferences.number())
									.then(context => context?.properties.number?.ref)
									.then(context => context?.properties.set_type?.state.node.output.set_type())
									.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result;
							}
							else if (value > 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(() => application.Cnumber_set_type.Ppositive).result;
							}
							else if (value == 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.inferences.number())
									.then(context => context?.properties.number?.ref)
									.then(context => context?.properties.set_type?.state.node.output.set_type())
									.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result;
							}
							else {
								throw new Error(`Missing case handler.`);
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstate_group__type__yes {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Ctext__type__yes {
}
export namespace Cnode_query_result {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__node_query_result, init:Tproperties__node_query_result},Cnode_query_result> {
		protected initialize(parent:Cnode_query_result, key:string, entry_init:Tproperties__node_query_result) { return new Cproperties__node_query_result(key, entry_init, parent); }
		protected finalize = finalize_properties__node_query_result
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_reply__node_query_result__properties_key_nval:client_request.Cproperties__node_query):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.node_query())
			.then(context => {
				for (let [key,val] of context?.properties.properties) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tnode_query_result['properties'], parent:Cnode_query_result) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__node_query_result {
	export class Dresult extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__result, init:Tdoes_not_exist__result}|
		{ name: 'exists', node:Cexists__result, init:Texists__result}|
		{ name: 'permission denied', node:Cpermission_denied__result__properties, init:Tpermission_denied__result__properties}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__result, parent:Cproperties__node_query_result) => new Cdoes_not_exist__result(init, parent);
				case 'exists': return (init:Texists__result, parent:Cproperties__node_query_result) => new Cexists__result(init, parent);
				case 'permission denied': return (init:Tpermission_denied__result__properties, parent:Cproperties__node_query_result) => new Cpermission_denied__result__properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__result;
				case 'exists': return finalize_exists__result;
				case 'permission denied': return finalize_permission_denied__result__properties;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__node_query_result['result'], parent:Cproperties__node_query_result) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Cexists__result {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__exists, init:Tcollection__type__exists}|
		{ name: 'file', node:Cfile__type__exists, init:Tfile__type__exists}|
		{ name: 'id path', node:Cid_path__type, init:Tid_path__type}|
		{ name: 'number', node:Cnumber__type__exists, init:Tnumber__type__exists}|
		{ name: 'state group', node:Cstate_group__type__exists, init:Tstate_group__type__exists}|
		{ name: 'text', node:Ctext__type__exists, init:Ttext__type__exists}> {
		protected initializer(state: 'collection'|'file'|'id path'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__exists, parent:Cexists__result) => new Ccollection__type__exists(init, parent);
				case 'file': return (init:Tfile__type__exists, parent:Cexists__result) => new Cfile__type__exists(init, parent);
				case 'id path': return (init:Tid_path__type, parent:Cexists__result) => new Cid_path__type(init, parent);
				case 'number': return (init:Tnumber__type__exists, parent:Cexists__result) => new Cnumber__type__exists(init, parent);
				case 'state group': return (init:Tstate_group__type__exists, parent:Cexists__result) => new Cstate_group__type__exists(init, parent);
				case 'text': return (init:Ttext__type__exists, parent:Cexists__result) => new Ctext__type__exists(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'id path'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__exists;
				case 'file': return finalize_file__type__exists;
				case 'id path': return finalize_id_path__type;
				case 'number': return finalize_number__type__exists;
				case 'state group': return finalize_state_group__type__exists;
				case 'text': return finalize_text__type__exists;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texists__result['type'], parent:Cexists__result) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__exists {
	export class Dentries extends AlanDictionary<{ node:Centries__collection__type__exists, init:Tentries__collection__type__exists},Ccollection__type__exists> {
		protected initialize(parent:Ccollection__type__exists, key:string, entry_init:Tentries__collection__type__exists) { return new Centries__collection__type__exists(key, entry_init, parent); }
		protected finalize = finalize_entries__collection__type__exists
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tcollection__type__exists['entries'], parent:Ccollection__type__exists) {
			super(data, parent);
		}
	}
	export class Dhas_graph_endpoints extends StateGroup<
		{ name: 'no', node:Cno__has_graph_endpoints, init:Tno__has_graph_endpoints}|
		{ name: 'yes', node:Cyes__has_graph_endpoints, init:Tyes__has_graph_endpoints}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_graph_endpoints, parent:Ccollection__type__exists) => new Cno__has_graph_endpoints(init, parent);
				case 'yes': return (init:Tyes__has_graph_endpoints, parent:Ccollection__type__exists) => new Cyes__has_graph_endpoints(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_graph_endpoints;
				case 'yes': return finalize_yes__has_graph_endpoints;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__exists['has graph endpoints'], parent:Ccollection__type__exists) {
			super(data, parent, 'has graph endpoints');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has graph endpoints'`; }
	}
}
export namespace Centries__collection__type__exists {
	export class Dproperties extends Cnode_query_result {
		constructor(data:Tentries__collection__type__exists['properties'], parent:Centries__collection__type__exists) {
			super(data, parent, {
				node_query: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.collection())
					.then(context => context?.properties.properties).result!)
			}, 'properties')
		}
	}
}
export namespace Cyes__has_graph_endpoints {
	export class Dordered_graphs extends AlanDictionary<{ node:Cordered_graphs, init:Tordered_graphs},Cyes__has_graph_endpoints> {
		protected initialize(parent:Cyes__has_graph_endpoints, key:string, entry_init:Tordered_graphs) { return new Cordered_graphs(key, entry_init, parent); }
		protected finalize = finalize_ordered_graphs
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_reply__node_query_result__properties__result__exists__type__collection__has_graph_endpoints__yes__ordered_graphs_key_nval:application.Cordered):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.graph_constraints)
			.then(context => {
				for (let [key,val] of context?.properties.graphs) {
					let tail_obj = resolve(val)
					.then(context => {
						if (context?.properties.type.state.name === 'ordered') {
							return context.properties.type.state.node as application.Cordered;
						} else {
							return undefined;
						}
					}).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'`; }
		constructor(data:Tyes__has_graph_endpoints['ordered graphs'], parent:Cyes__has_graph_endpoints) {
			super(data, parent);
		}
	}
}
export namespace Cordered_graphs {
}
export namespace Cfile__type__exists {
}
export namespace Cid_path__type {
	export class Did_path extends Cid_path__client_reply {
		constructor(data:Tid_path__type['id path'], parent:Cid_path__type) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'id path')
		}
	}
}
export namespace Cnumber__type__exists {
	export class Dvalue extends AlanInteger {
		constructor(data:Tnumber__type__exists['value'], parent:Cnumber__type__exists) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstate_group__type__exists {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group__type__exists) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Ctext__type__exists {
}
export namespace Cnotification_data {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__notification_data, init:Tproperties__notification_data},Cnotification_data> {
		protected initialize(parent:Cnotification_data, key:string, entry_init:Tproperties__notification_data) { return new Cproperties__notification_data(key, entry_init, parent); }
		protected finalize = finalize_properties__notification_data
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tnotification_data['properties'], parent:Cnotification_data) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__notification_data {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__properties, init:Tcollection__type__properties}|
		{ name: 'file', node:Cfile__type__properties, init:Tfile__type__properties}|
		{ name: 'group', node:Cgroup__type__properties, init:Tgroup__type__properties}|
		{ name: 'number', node:Cnumber__type__properties, init:Tnumber__type__properties}|
		{ name: 'state group', node:Cstate_group__type__properties, init:Tstate_group__type__properties}|
		{ name: 'text', node:Ctext__type__properties, init:Ttext__type__properties}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__properties, parent:Cproperties__notification_data) => new Ccollection__type__properties(init, parent);
				case 'file': return (init:Tfile__type__properties, parent:Cproperties__notification_data) => new Cfile__type__properties(init, parent);
				case 'group': return (init:Tgroup__type__properties, parent:Cproperties__notification_data) => new Cgroup__type__properties(init, parent);
				case 'number': return (init:Tnumber__type__properties, parent:Cproperties__notification_data) => new Cnumber__type__properties(init, parent);
				case 'state group': return (init:Tstate_group__type__properties, parent:Cproperties__notification_data) => new Cstate_group__type__properties(init, parent);
				case 'text': return (init:Ttext__type__properties, parent:Cproperties__notification_data) => new Ctext__type__properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__properties;
				case 'file': return finalize_file__type__properties;
				case 'group': return finalize_group__type__properties;
				case 'number': return finalize_number__type__properties;
				case 'state group': return finalize_state_group__type__properties;
				case 'text': return finalize_text__type__properties;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__notification_data['type'], parent:Cproperties__notification_data) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__properties {
	export class Dgraph_endpoints_update extends StateGroup<
		{ name: 'no', node:Cno__graph_endpoints_update, init:Tno__graph_endpoints_update}|
		{ name: 'yes', node:Cyes__graph_endpoints_update, init:Tyes__graph_endpoints_update}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__graph_endpoints_update, parent:Ccollection__type__properties) => new Cno__graph_endpoints_update(init, parent);
				case 'yes': return (init:Tyes__graph_endpoints_update, parent:Ccollection__type__properties) => new Cyes__graph_endpoints_update(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__graph_endpoints_update;
				case 'yes': return finalize_yes__graph_endpoints_update;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__properties['graph endpoints update'], parent:Ccollection__type__properties) {
			super(data, parent, 'graph endpoints update');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph endpoints update'`; }
	}
	export class Dvalue_update extends StateGroup<
		{ name: 'entries', node:Centries__value_update, init:Tentries__value_update}|
		{ name: 'no', node:Cno__value_update__collection, init:Tno__value_update__collection}|
		{ name: 'updated', node:Cupdated, init:Tupdated}> {
		protected initializer(state: 'entries'|'no'|'updated') {
			switch (state) {
				case 'entries': return (init:Tentries__value_update, parent:Ccollection__type__properties) => new Centries__value_update(init, parent);
				case 'no': return (init:Tno__value_update__collection, parent:Ccollection__type__properties) => new Cno__value_update__collection(init, parent);
				case 'updated': return (init:Tupdated, parent:Ccollection__type__properties) => new Cupdated(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entries'|'no'|'updated') {
			switch (state) {
				case 'entries': return finalize_entries__value_update;
				case 'no': return finalize_no__value_update__collection;
				case 'updated': return finalize_updated;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__properties['value update'], parent:Ccollection__type__properties) {
			super(data, parent, 'value update');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value update'`; }
	}
}
export namespace Cyes__graph_endpoints_update {
	export class Dresult extends StateGroup<
		{ name: 'endpoints', node:Cendpoints__result__yes__graph_endpoints_update, init:Tendpoints__result__yes__graph_endpoints_update}|
		{ name: 'none', node:Cnone__result__yes__graph_endpoints_update, init:Tnone__result__yes__graph_endpoints_update}> {
		protected initializer(state: 'endpoints'|'none') {
			switch (state) {
				case 'endpoints': return (init:Tendpoints__result__yes__graph_endpoints_update, parent:Cyes__graph_endpoints_update) => new Cendpoints__result__yes__graph_endpoints_update(init, parent);
				case 'none': return (init:Tnone__result__yes__graph_endpoints_update, parent:Cyes__graph_endpoints_update) => new Cnone__result__yes__graph_endpoints_update(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'endpoints'|'none') {
			switch (state) {
				case 'endpoints': return finalize_endpoints__result__yes__graph_endpoints_update;
				case 'none': return finalize_none__result__yes__graph_endpoints_update;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__graph_endpoints_update['result'], parent:Cyes__graph_endpoints_update) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Cendpoints__result__yes__graph_endpoints_update {
	export class Dgraph_endpoints extends AlanDictionary<{ node:Cgraph_endpoints__endpoints__result__yes__graph_endpoints_update, init:Tgraph_endpoints__endpoints__result__yes__graph_endpoints_update},Cendpoints__result__yes__graph_endpoints_update> {
		protected initialize(parent:Cendpoints__result__yes__graph_endpoints_update, key:string, entry_init:Tgraph_endpoints__endpoints__result__yes__graph_endpoints_update) { return new Cgraph_endpoints__endpoints__result__yes__graph_endpoints_update(key, entry_init, parent); }
		protected finalize = finalize_graph_endpoints__endpoints__result__yes__graph_endpoints_update
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph endpoints'`; }
		constructor(data:Tendpoints__result__yes__graph_endpoints_update['graph endpoints'], parent:Cendpoints__result__yes__graph_endpoints_update) {
			super(data, parent);
		}
	}
}
export namespace Cgraph_endpoints__endpoints__result__yes__graph_endpoints_update {
}
export namespace Centries__value_update {
	export class Dentries extends AlanDictionary<{ node:Centries__entries__value_update, init:Tentries__entries__value_update},Centries__value_update> {
		protected initialize(parent:Centries__value_update, key:string, entry_init:Tentries__entries__value_update) { return new Centries__entries__value_update(key, entry_init, parent); }
		protected finalize = finalize_entries__entries__value_update
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tentries__value_update['entries'], parent:Centries__value_update) {
			super(data, parent);
		}
	}
}
export namespace Centries__entries__value_update {
	export class Dtype extends StateGroup<
		{ name: 'create', node:Ccreate, init:Tcreate}|
		{ name: 'remove', node:Cremove, init:Tremove}|
		{ name: 'update', node:Cupdate__type__entries, init:Tupdate__type__entries}> {
		protected initializer(state: 'create'|'remove'|'update') {
			switch (state) {
				case 'create': return (init:Tcreate, parent:Centries__entries__value_update) => new Ccreate(init, parent);
				case 'remove': return (init:Tremove, parent:Centries__entries__value_update) => new Cremove(init, parent);
				case 'update': return (init:Tupdate__type__entries, parent:Centries__entries__value_update) => new Cupdate__type__entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'create'|'remove'|'update') {
			switch (state) {
				case 'create': return finalize_create;
				case 'remove': return finalize_remove;
				case 'update': return finalize_update__type__entries;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__entries__value_update['type'], parent:Centries__entries__value_update) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccreate {
	export class Ddelete_permitted extends Cpermission_data {
		constructor(data:Tcreate['delete permitted'], parent:Ccreate) {
			super(data, parent, 'delete permitted')
		}
	}
	export class Dnode_data extends Csubscription_result_data {
		constructor(data:Tcreate['node data'], parent:Ccreate) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.subscribe_on_entries())
					.then(context => context?.properties.subscription).result!)
			}, 'node data')
		}
	}
}
export namespace Cupdate__type__entries {
	export class Dnode_data extends Cnotification_data {
		constructor(data:Tupdate__type__entries['node data'], parent:Cupdate__type__entries) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.subscribe_on_entries())
					.then(context => context?.properties.subscription).result!)
			}, 'node data')
		}
	}
}
export namespace Cfile__type__properties {
}
export namespace Cgroup__type__properties {
	export class Dnotification_data extends Cnotification_data {
		constructor(data:Tgroup__type__properties['notification data'], parent:Cgroup__type__properties) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group())
					.then(context => context?.properties.subscription).result!)
			}, 'notification data')
		}
	}
}
export namespace Cnumber__type__properties {
	export class Dvalue extends AlanInteger {
		constructor(data:Tnumber__type__properties['value'], parent:Cnumber__type__properties) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstate_group__type__properties {
	export class Dstate extends Reference<client_request.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group__type__properties) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'set', node:Cset, init:Tset}|
		{ name: 'update', node:Cupdate__type__state_group, init:Tupdate__type__state_group}> {
		protected initializer(state: 'set'|'update') {
			switch (state) {
				case 'set': return (init:Tset, parent:Cstate_group__type__properties) => new Cset(init, parent);
				case 'update': return (init:Tupdate__type__state_group, parent:Cstate_group__type__properties) => new Cupdate__type__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'set'|'update') {
			switch (state) {
				case 'set': return finalize_set;
				case 'update': return finalize_update__type__state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__properties['type'], parent:Cstate_group__type__properties) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cset {
	export class Ddelete_permitted extends Cpermission_data {
		constructor(data:Tset['delete permitted'], parent:Cset) {
			super(data, parent, 'delete permitted')
		}
	}
	export class Dinitialization_data extends Csubscription_result_data {
		constructor(data:Tset['initialization data'], parent:Cset) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.subscription).result!)
			}, 'initialization data')
		}
	}
}
export namespace Cupdate__type__state_group {
	export class Dnotification_data extends Cnotification_data {
		constructor(data:Tupdate__type__state_group['notification data'], parent:Cupdate__type__state_group) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.subscription).result!)
			}, 'notification data')
		}
	}
}
export namespace Ctext__type__properties {
	export class Dreference_update extends StateGroup<
		{ name: 'no', node:Cno__reference_update, init:Tno__reference_update}|
		{ name: 'yes', node:Cyes__reference_update, init:Tyes__reference_update}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__reference_update, parent:Ctext__type__properties) => new Cno__reference_update(init, parent);
				case 'yes': return (init:Tyes__reference_update, parent:Ctext__type__properties) => new Cyes__reference_update(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__reference_update;
				case 'yes': return finalize_yes__reference_update;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__properties['reference update'], parent:Ctext__type__properties) {
			super(data, parent, 'reference update');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'reference update'`; }
	}
	export class Dvalue_update extends StateGroup<
		{ name: 'no', node:Cno__value_update__text, init:Tno__value_update__text}|
		{ name: 'yes', node:Cyes__value_update, init:Tyes__value_update}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__value_update__text, parent:Ctext__type__properties) => new Cno__value_update__text(init, parent);
				case 'yes': return (init:Tyes__value_update, parent:Ctext__type__properties) => new Cyes__value_update(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__value_update__text;
				case 'yes': return finalize_yes__value_update;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__properties['value update'], parent:Ctext__type__properties) {
			super(data, parent, 'value update');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value update'`; }
	}
}
export namespace Cyes__reference_update {
	export class Dresult extends StateGroup<
		{ name: 'disconnected', node:Cdisconnected__result__yes__reference_update, init:Tdisconnected__result__yes__reference_update}|
		{ name: 'node', node:Cnode__result__yes__reference_update, init:Tnode__result__yes__reference_update}|
		{ name: 'permission denied', node:Cpermission_denied__result__yes__reference_update, init:Tpermission_denied__result__yes__reference_update}> {
		protected initializer(state: 'disconnected'|'node'|'permission denied') {
			switch (state) {
				case 'disconnected': return (init:Tdisconnected__result__yes__reference_update, parent:Cyes__reference_update) => new Cdisconnected__result__yes__reference_update(init, parent);
				case 'node': return (init:Tnode__result__yes__reference_update, parent:Cyes__reference_update) => new Cnode__result__yes__reference_update(init, parent);
				case 'permission denied': return (init:Tpermission_denied__result__yes__reference_update, parent:Cyes__reference_update) => new Cpermission_denied__result__yes__reference_update(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'disconnected'|'node'|'permission denied') {
			switch (state) {
				case 'disconnected': return finalize_disconnected__result__yes__reference_update;
				case 'node': return finalize_node__result__yes__reference_update;
				case 'permission denied': return finalize_permission_denied__result__yes__reference_update;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__reference_update['result'], parent:Cyes__reference_update) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Cnode__result__yes__reference_update {
	export class Did_path extends Cid_path__client_reply {
		constructor(data:Tnode__result__yes__reference_update['id path'], parent:Cnode__result__yes__reference_update) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'id path')
		}
	}
}
export namespace Cyes__value_update {
}
export namespace Cpermission_data {
	export class Dpermitted extends StateGroup<
		{ name: 'no', node:Cno__permitted, init:Tno__permitted}|
		{ name: 'yes', node:Cyes__permitted, init:Tyes__permitted}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__permitted, parent:Cpermission_data) => new Cno__permitted(init, parent);
				case 'yes': return (init:Tyes__permitted, parent:Cpermission_data) => new Cyes__permitted(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__permitted;
				case 'yes': return finalize_yes__permitted;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tpermission_data['permitted'], parent:Cpermission_data) {
			super(data, parent, 'permitted');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'permitted'`; }
	}
}
export namespace Csubscription_result_data {
	export class Dread_permitted extends StateGroup<
		{ name: 'no', node:Cno__read_permitted, init:Tno__read_permitted}|
		{ name: 'yes', node:Cyes__read_permitted, init:Tyes__read_permitted}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__read_permitted, parent:Csubscription_result_data) => new Cno__read_permitted(init, parent);
				case 'yes': return (init:Tyes__read_permitted, parent:Csubscription_result_data) => new Cyes__read_permitted(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__read_permitted;
				case 'yes': return finalize_yes__read_permitted;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsubscription_result_data['read permitted'], parent:Csubscription_result_data) {
			super(data, parent, 'read permitted');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'read permitted'`; }
	}
	export class Dupdate_permitted extends Cpermission_data {
		constructor(data:Tsubscription_result_data['update permitted'], parent:Csubscription_result_data) {
			super(data, parent, 'update permitted')
		}
	}
}
export namespace Cyes__read_permitted {
	export class Dattributes extends AlanDictionary<{ node:Cattributes, init:Tattributes},Cyes__read_permitted> {
		protected initialize(parent:Cyes__read_permitted, key:string, entry_init:Tattributes) { return new Cattributes(key, entry_init, parent); }
		protected finalize = finalize_attributes
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_reply__subscription_result_data__read_permitted__yes__attributes_key_nval:client_request.Cattributes):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.node_subscription())
			.then(context => {
				for (let [key,val] of context?.properties.attributes) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Tyes__read_permitted['attributes'], parent:Cyes__read_permitted) {
			super(data, parent);
		}
	}
}
export namespace Cattributes {
	export class Dtype extends StateGroup<
		{ name: 'command', node:Ccommand, init:Tcommand}|
		{ name: 'property', node:Cproperty, init:Tproperty}> {
		protected initializer(state: 'command'|'property') {
			switch (state) {
				case 'command': return (init:Tcommand, parent:Cattributes) => new Ccommand(init, parent);
				case 'property': return (init:Tproperty, parent:Cattributes) => new Cproperty(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'command'|'property') {
			switch (state) {
				case 'command': return finalize_command;
				case 'property': return finalize_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes['type'], parent:Cattributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccommand {
	export class Dexecute_permitted extends Cpermission_data {
		constructor(data:Tcommand['execute permitted'], parent:Ccommand) {
			super(data, parent, 'execute permitted')
		}
	}
}
export namespace Cproperty {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__property, init:Tcollection__type__property}|
		{ name: 'file', node:Cfile__type__property, init:Tfile__type__property}|
		{ name: 'group', node:Cgroup__type__property, init:Tgroup__type__property}|
		{ name: 'number', node:Cnumber__type__property, init:Tnumber__type__property}|
		{ name: 'state group', node:Cstate_group__type__property, init:Tstate_group__type__property}|
		{ name: 'text', node:Ctext__type__property, init:Ttext__type__property}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__property, parent:Cproperty) => new Ccollection__type__property(init, parent);
				case 'file': return (init:Tfile__type__property, parent:Cproperty) => new Cfile__type__property(init, parent);
				case 'group': return (init:Tgroup__type__property, parent:Cproperty) => new Cgroup__type__property(init, parent);
				case 'number': return (init:Tnumber__type__property, parent:Cproperty) => new Cnumber__type__property(init, parent);
				case 'state group': return (init:Tstate_group__type__property, parent:Cproperty) => new Cstate_group__type__property(init, parent);
				case 'text': return (init:Ttext__type__property, parent:Cproperty) => new Ctext__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__property;
				case 'file': return finalize_file__type__property;
				case 'group': return finalize_group__type__property;
				case 'number': return finalize_number__type__property;
				case 'state group': return finalize_state_group__type__property;
				case 'text': return finalize_text__type__property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty['type'], parent:Cproperty) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__property {
	export class Dcreate_permitted extends Cpermission_data {
		constructor(data:Tcollection__type__property['create permitted'], parent:Ccollection__type__property) {
			super(data, parent, 'create permitted')
		}
	}
	export class Dgraph_endpoints_result extends StateGroup<
		{ name: 'no', node:Cno__graph_endpoints_result, init:Tno__graph_endpoints_result}|
		{ name: 'yes', node:Cyes__graph_endpoints_result, init:Tyes__graph_endpoints_result}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__graph_endpoints_result, parent:Ccollection__type__property) => new Cno__graph_endpoints_result(init, parent);
				case 'yes': return (init:Tyes__graph_endpoints_result, parent:Ccollection__type__property) => new Cyes__graph_endpoints_result(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__graph_endpoints_result;
				case 'yes': return finalize_yes__graph_endpoints_result;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property['graph endpoints result'], parent:Ccollection__type__property) {
			super(data, parent, 'graph endpoints result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph endpoints result'`; }
	}
	export class Dresult extends StateGroup<
		{ name: 'entries', node:Centries__result, init:Tentries__result}|
		{ name: 'update', node:Cupdate__result, init:Tupdate__result}> {
		protected initializer(state: 'entries'|'update') {
			switch (state) {
				case 'entries': return (init:Tentries__result, parent:Ccollection__type__property) => new Centries__result(init, parent);
				case 'update': return (init:Tupdate__result, parent:Ccollection__type__property) => new Cupdate__result(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entries'|'update') {
			switch (state) {
				case 'entries': return finalize_entries__result;
				case 'update': return finalize_update__result;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property['result'], parent:Ccollection__type__property) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Cyes__graph_endpoints_result {
	export class Dresult extends StateGroup<
		{ name: 'endpoints', node:Cendpoints__result__yes__graph_endpoints_result, init:Tendpoints__result__yes__graph_endpoints_result}|
		{ name: 'none', node:Cnone__result__yes__graph_endpoints_result, init:Tnone__result__yes__graph_endpoints_result}> {
		protected initializer(state: 'endpoints'|'none') {
			switch (state) {
				case 'endpoints': return (init:Tendpoints__result__yes__graph_endpoints_result, parent:Cyes__graph_endpoints_result) => new Cendpoints__result__yes__graph_endpoints_result(init, parent);
				case 'none': return (init:Tnone__result__yes__graph_endpoints_result, parent:Cyes__graph_endpoints_result) => new Cnone__result__yes__graph_endpoints_result(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'endpoints'|'none') {
			switch (state) {
				case 'endpoints': return finalize_endpoints__result__yes__graph_endpoints_result;
				case 'none': return finalize_none__result__yes__graph_endpoints_result;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__graph_endpoints_result['result'], parent:Cyes__graph_endpoints_result) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Cendpoints__result__yes__graph_endpoints_result {
	export class Dgraph_endpoints extends AlanDictionary<{ node:Cgraph_endpoints__endpoints__result__yes__graph_endpoints_result, init:Tgraph_endpoints__endpoints__result__yes__graph_endpoints_result},Cendpoints__result__yes__graph_endpoints_result> {
		protected initialize(parent:Cendpoints__result__yes__graph_endpoints_result, key:string, entry_init:Tgraph_endpoints__endpoints__result__yes__graph_endpoints_result) { return new Cgraph_endpoints__endpoints__result__yes__graph_endpoints_result(key, entry_init, parent); }
		protected finalize = finalize_graph_endpoints__endpoints__result__yes__graph_endpoints_result
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_reply__subscription_result_data__read_permitted__yes__attributes__type__property__type__collection__graph_endpoints_result__yes__result__endpoints__graph_endpoints_key_nval:application.Cordered):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.collection())
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.graph_constraints)
			.then(context => {
				for (let [key,val] of context?.properties.graphs) {
					let tail_obj = resolve(val)
					.then(context => {
						if (context?.properties.type.state.name === 'ordered') {
							return context.properties.type.state.node as application.Cordered;
						} else {
							return undefined;
						}
					}).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph endpoints'`; }
		constructor(data:Tendpoints__result__yes__graph_endpoints_result['graph endpoints'], parent:Cendpoints__result__yes__graph_endpoints_result) {
			super(data, parent);
		}
	}
}
export namespace Cgraph_endpoints__endpoints__result__yes__graph_endpoints_result {
}
export namespace Centries__result {
	export class Dentries extends AlanDictionary<{ node:Centries__entries__result, init:Tentries__entries__result},Centries__result> {
		protected initialize(parent:Centries__result, key:string, entry_init:Tentries__entries__result) { return new Centries__entries__result(key, entry_init, parent); }
		protected finalize = finalize_entries__entries__result
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tentries__result['entries'], parent:Centries__result) {
			super(data, parent);
		}
	}
}
export namespace Centries__entries__result {
	export class Ddelete_permitted extends Cpermission_data {
		constructor(data:Tentries__entries__result['delete permitted'], parent:Centries__entries__result) {
			super(data, parent, 'delete permitted')
		}
	}
	export class Dnode_data extends Csubscription_result_data {
		constructor(data:Tentries__entries__result['node data'], parent:Centries__entries__result) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.subscribe_on_entries())
					.then(context => context?.properties.subscription).result!)
			}, 'node data')
		}
	}
}
export namespace Cfile__type__property {
}
export namespace Cgroup__type__property {
	export class Dnode_data extends Csubscription_result_data {
		constructor(data:Tgroup__type__property['node data'], parent:Cgroup__type__property) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group())
					.then(context => context?.properties.subscription).result!)
			}, 'node data')
		}
	}
}
export namespace Cnumber__type__property {
	export class Dvalue extends AlanInteger {
		constructor(data:Tnumber__type__property['value'], parent:Cnumber__type__property) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstate_group__type__property {
	export class Dcreate_permitted extends AlanDictionary<{ node:Ccreate_permitted, init:Tcreate_permitted},Cstate_group__type__property> {
		protected initialize(parent:Cstate_group__type__property, key:string) { return new Ccreate_permitted(key, {}, parent); }
		protected finalize = finalize_create_permitted
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'create permitted'`; }
		constructor(data:Tstate_group__type__property['create permitted'], parent:Cstate_group__type__property) {
			super(data, parent);
		}
	}
	export class Ddelete_permitted extends Cpermission_data {
		constructor(data:Tstate_group__type__property['delete permitted'], parent:Cstate_group__type__property) {
			super(data, parent, 'delete permitted')
		}
	}
	export class Dnode_data extends Csubscription_result_data {
		constructor(data:Tstate_group__type__property['node data'], parent:Cstate_group__type__property) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.subscription).result!)
			}, 'node data')
		}
	}
	export class Dstate extends Reference<client_request.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group__type__property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Ctext__type__property {
	export class Dreference_result extends StateGroup<
		{ name: 'no', node:Cno__reference_result__text, init:Tno__reference_result__text}|
		{ name: 'yes', node:Cyes__reference_result__text, init:Tyes__reference_result__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__reference_result__text, parent:Ctext__type__property) => new Cno__reference_result__text(init, parent);
				case 'yes': return (init:Tyes__reference_result__text, parent:Ctext__type__property) => new Cyes__reference_result__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__reference_result__text;
				case 'yes': return finalize_yes__reference_result__text;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['reference result'], parent:Ctext__type__property) {
			super(data, parent, 'reference result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'reference result'`; }
	}
}
export namespace Cyes__reference_result__text {
	export class Dresult extends StateGroup<
		{ name: 'disconnected', node:Cdisconnected__result__yes__reference_result, init:Tdisconnected__result__yes__reference_result}|
		{ name: 'node', node:Cnode__result__yes__reference_result, init:Tnode__result__yes__reference_result}|
		{ name: 'permission denied', node:Cpermission_denied__result__yes__reference_result, init:Tpermission_denied__result__yes__reference_result}> {
		protected initializer(state: 'disconnected'|'node'|'permission denied') {
			switch (state) {
				case 'disconnected': return (init:Tdisconnected__result__yes__reference_result, parent:Cyes__reference_result__text) => new Cdisconnected__result__yes__reference_result(init, parent);
				case 'node': return (init:Tnode__result__yes__reference_result, parent:Cyes__reference_result__text) => new Cnode__result__yes__reference_result(init, parent);
				case 'permission denied': return (init:Tpermission_denied__result__yes__reference_result, parent:Cyes__reference_result__text) => new Cpermission_denied__result__yes__reference_result(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'disconnected'|'node'|'permission denied') {
			switch (state) {
				case 'disconnected': return finalize_disconnected__result__yes__reference_result;
				case 'node': return finalize_node__result__yes__reference_result;
				case 'permission denied': return finalize_permission_denied__result__yes__reference_result;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__reference_result__text['result'], parent:Cyes__reference_result__text) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Cnode__result__yes__reference_result {
	export class Did_path extends Cid_path__client_reply {
		constructor(data:Tnode__result__yes__reference_result['id path'], parent:Cnode__result__yes__reference_result) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'id path')
		}
	}
}
export namespace Cclient_reply {
	export class Dtype extends StateGroup<
		{ name: 'acyclic graph list query', node:Cacyclic_graph_list_query, init:Tacyclic_graph_list_query}|
		{ name: 'acyclic graph tree query', node:Cacyclic_graph_tree_query, init:Tacyclic_graph_tree_query}|
		{ name: 'collection query', node:Ccollection_query, init:Tcollection_query}|
		{ name: 'command execution', node:Ccommand_execution, init:Tcommand_execution}|
		{ name: 'mutation', node:Cmutation, init:Tmutation}|
		{ name: 'notification', node:Cnotification, init:Tnotification}|
		{ name: 'password mutation', node:Cpassword_mutation, init:Tpassword_mutation}|
		{ name: 'password reset', node:Cpassword_reset, init:Tpassword_reset}|
		{ name: 'subscription', node:Csubscription, init:Tsubscription}> {
		protected initializer(state: 'acyclic graph list query'|'acyclic graph tree query'|'collection query'|'command execution'|'mutation'|'notification'|'password mutation'|'password reset'|'subscription') {
			switch (state) {
				case 'acyclic graph list query': return (init:Tacyclic_graph_list_query, parent:Cclient_reply) => new Cacyclic_graph_list_query(init, parent);
				case 'acyclic graph tree query': return (init:Tacyclic_graph_tree_query, parent:Cclient_reply) => new Cacyclic_graph_tree_query(init, parent);
				case 'collection query': return (init:Tcollection_query, parent:Cclient_reply) => new Ccollection_query(init, parent);
				case 'command execution': return (init:Tcommand_execution, parent:Cclient_reply) => new Ccommand_execution(init, parent);
				case 'mutation': return (init:Tmutation, parent:Cclient_reply) => new Cmutation(init, parent);
				case 'notification': return (init:Tnotification, parent:Cclient_reply) => new Cnotification(init, parent);
				case 'password mutation': return (init:Tpassword_mutation, parent:Cclient_reply) => new Cpassword_mutation(init, parent);
				case 'password reset': return (init:Tpassword_reset, parent:Cclient_reply) => new Cpassword_reset(init, parent);
				case 'subscription': return (init:Tsubscription, parent:Cclient_reply) => new Csubscription(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'acyclic graph list query'|'acyclic graph tree query'|'collection query'|'command execution'|'mutation'|'notification'|'password mutation'|'password reset'|'subscription') {
			switch (state) {
				case 'acyclic graph list query': return finalize_acyclic_graph_list_query;
				case 'acyclic graph tree query': return finalize_acyclic_graph_tree_query;
				case 'collection query': return finalize_collection_query;
				case 'command execution': return finalize_command_execution;
				case 'mutation': return finalize_mutation;
				case 'notification': return finalize_notification;
				case 'password mutation': return finalize_password_mutation;
				case 'password reset': return finalize_password_reset;
				case 'subscription': return finalize_subscription;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_reply['type'], parent:Cclient_reply) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cacyclic_graph_list_query {
	export class Dcontext extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__context__acyclic_graph_list_query, init:Tdoes_not_exist__context__acyclic_graph_list_query}|
		{ name: 'exists', node:Cexists__context__acyclic_graph_list_query, init:Texists__context__acyclic_graph_list_query}|
		{ name: 'permission denied', node:Cpermission_denied__context__acyclic_graph_list_query, init:Tpermission_denied__context__acyclic_graph_list_query}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__context__acyclic_graph_list_query, parent:Cacyclic_graph_list_query) => new Cdoes_not_exist__context__acyclic_graph_list_query(init, parent);
				case 'exists': return (init:Texists__context__acyclic_graph_list_query, parent:Cacyclic_graph_list_query) => new Cexists__context__acyclic_graph_list_query(init, parent);
				case 'permission denied': return (init:Tpermission_denied__context__acyclic_graph_list_query, parent:Cacyclic_graph_list_query) => new Cpermission_denied__context__acyclic_graph_list_query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__context__acyclic_graph_list_query;
				case 'exists': return finalize_exists__context__acyclic_graph_list_query;
				case 'permission denied': return finalize_permission_denied__context__acyclic_graph_list_query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tacyclic_graph_list_query['context'], parent:Cacyclic_graph_list_query) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cexists__context__acyclic_graph_list_query {
	export class Dentry_points extends Centry_points {
		constructor(data:Texists__context__acyclic_graph_list_query['entry points'], parent:Cexists__context__acyclic_graph_list_query) {
			super(data, parent, {
				application: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application).result!),
				entry_path: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.source())
					.then(context => context?.properties.entry_points).result!),
				list_query: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.source())
					.then(context => context?.properties.query).result!)
			}, 'entry points')
		}
	}
}
export namespace Cacyclic_graph_tree_query {
	export class Dcontext extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__context__acyclic_graph_tree_query, init:Tdoes_not_exist__context__acyclic_graph_tree_query}|
		{ name: 'exists', node:Cexists__context__acyclic_graph_tree_query, init:Texists__context__acyclic_graph_tree_query}|
		{ name: 'permission denied', node:Cpermission_denied__context__acyclic_graph_tree_query, init:Tpermission_denied__context__acyclic_graph_tree_query}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__context__acyclic_graph_tree_query, parent:Cacyclic_graph_tree_query) => new Cdoes_not_exist__context__acyclic_graph_tree_query(init, parent);
				case 'exists': return (init:Texists__context__acyclic_graph_tree_query, parent:Cacyclic_graph_tree_query) => new Cexists__context__acyclic_graph_tree_query(init, parent);
				case 'permission denied': return (init:Tpermission_denied__context__acyclic_graph_tree_query, parent:Cacyclic_graph_tree_query) => new Cpermission_denied__context__acyclic_graph_tree_query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__context__acyclic_graph_tree_query;
				case 'exists': return finalize_exists__context__acyclic_graph_tree_query;
				case 'permission denied': return finalize_permission_denied__context__acyclic_graph_tree_query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tacyclic_graph_tree_query['context'], parent:Cacyclic_graph_tree_query) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cexists__context__acyclic_graph_tree_query {
	export class Dgraph extends Cgraph_node {
		constructor(data:Texists__context__acyclic_graph_tree_query['graph'], parent:Cexists__context__acyclic_graph_tree_query) {
			super(data, parent, 'graph')
		}
	}
}
export namespace Ccollection_query {
	export class Dcontext extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__context__collection_query, init:Tdoes_not_exist__context__collection_query}|
		{ name: 'exists', node:Cexists__context__collection_query, init:Texists__context__collection_query}|
		{ name: 'permission denied', node:Cpermission_denied__context__collection_query, init:Tpermission_denied__context__collection_query}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__context__collection_query, parent:Ccollection_query) => new Cdoes_not_exist__context__collection_query(init, parent);
				case 'exists': return (init:Texists__context__collection_query, parent:Ccollection_query) => new Cexists__context__collection_query(init, parent);
				case 'permission denied': return (init:Tpermission_denied__context__collection_query, parent:Ccollection_query) => new Cpermission_denied__context__collection_query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__context__collection_query;
				case 'exists': return finalize_exists__context__collection_query;
				case 'permission denied': return finalize_permission_denied__context__collection_query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_query['context'], parent:Ccollection_query) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cexists__context__collection_query {
	export class Daggregates extends AlanDictionary<{ node:Caggregates, init:Taggregates},Cexists__context__collection_query> {
		protected initialize(parent:Cexists__context__collection_query, key:string, entry_init:Taggregates) { return new Caggregates(key, entry_init, parent); }
		protected finalize = finalize_aggregates
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_reply__type__collection_query__context__exists__aggregates_key_nval:client_request.Caggregates):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.source())
			.then(context => {
				for (let [key,val] of context?.properties.aggregates) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'aggregates'`; }
		constructor(data:Texists__context__collection_query['aggregates'], parent:Cexists__context__collection_query) {
			super(data, parent);
		}
	}
	export class Damount_of_entries extends AlanInteger {
		constructor(data:Texists__context__collection_query['amount of entries'], parent:Cexists__context__collection_query) {
			number__is_non_negative(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'amount of entries'`; }
	}
	export class Dentries extends StateGroup<
		{ name: 'no', node:Cno__entries, init:Tno__entries}|
		{ name: 'yes', node:Cyes__entries, init:Tyes__entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__entries, parent:Cexists__context__collection_query) => new Cno__entries(init, parent);
				case 'yes': return (init:Tyes__entries, parent:Cexists__context__collection_query) => new Cyes__entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__entries;
				case 'yes': return finalize_yes__entries;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texists__context__collection_query['entries'], parent:Cexists__context__collection_query) {
			super(data, parent, 'entries');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
	}
}
export namespace Caggregates {
	export class Dtype extends StateGroup<
		{ name: 'state distribution', node:Cstate_distribution, init:Tstate_distribution}> {
		protected initializer(state: 'state distribution') {
			switch (state) {
				case 'state distribution': return (init:Tstate_distribution, parent:Caggregates) => new Cstate_distribution(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'state distribution') {
			switch (state) {
				case 'state distribution': return finalize_state_distribution;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Taggregates['type'], parent:Caggregates) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstate_distribution {
	export class Dstates extends AlanDictionary<{ node:Cstates, init:Tstates},Cstate_distribution> {
		protected initialize(parent:Cstate_distribution, key:string, entry_init:Tstates) { return new Cstates(key, entry_init, parent); }
		protected finalize = finalize_states
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_reply__type__collection_query__context__exists__aggregates__type__state_distribution__states_key_nval:application.Cstates__state_group):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.inferences.state_distribution_type())
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_distribution['states'], parent:Cstate_distribution) {
			super(data, parent);
		}
	}
}
export namespace Cstates {
	export class Damount extends AlanInteger {
		constructor(data:Tstates['amount'], parent:Cstates) {
			number__is_non_negative(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'amount'`; }
	}
}
export namespace Cyes__entries {
	export class Dentries extends Ccollection_query_entries {
		constructor(data:Tyes__entries['entries'], parent:Cyes__entries) {
			super(data, parent, {
				collection_path: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.source())
					.then(context => context?.properties.collection_path).result!),
				select_entries: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.select_entries()).result!)
			}, 'entries')
		}
	}
	export class Dlimit_exceeded extends StateGroup<
		{ name: 'no', node:Cno__limit_exceeded, init:Tno__limit_exceeded}|
		{ name: 'yes', node:Cyes__limit_exceeded, init:Tyes__limit_exceeded}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__limit_exceeded, parent:Cyes__entries) => new Cno__limit_exceeded(init, parent);
				case 'yes': return (init:Tyes__limit_exceeded, parent:Cyes__entries) => new Cyes__limit_exceeded(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__limit_exceeded;
				case 'yes': return finalize_yes__limit_exceeded;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__entries['limit exceeded'], parent:Cyes__entries) {
			super(data, parent, 'limit exceeded');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'limit exceeded'`; }
	}
}
export namespace Cmutation {
	export class Dcontext extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__context__mutation, init:Tdoes_not_exist__context__mutation}|
		{ name: 'exists', node:Cexists__context__mutation, init:Texists__context__mutation}|
		{ name: 'permission denied', node:Cpermission_denied__context__mutation, init:Tpermission_denied__context__mutation}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__context__mutation, parent:Cmutation) => new Cdoes_not_exist__context__mutation(init, parent);
				case 'exists': return (init:Texists__context__mutation, parent:Cmutation) => new Cexists__context__mutation(init, parent);
				case 'permission denied': return (init:Tpermission_denied__context__mutation, parent:Cmutation) => new Cpermission_denied__context__mutation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__context__mutation;
				case 'exists': return finalize_exists__context__mutation;
				case 'permission denied': return finalize_permission_denied__context__mutation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tmutation['context'], parent:Cmutation) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cexists__context__mutation {
	export class Dresult extends StateGroup<
		{ name: 'failure', node:Cfailure__result__exists__context, init:Tfailure__result__exists__context}|
		{ name: 'success', node:Csuccess__result__exists__context, init:Tsuccess__result__exists__context}> {
		protected initializer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return (init:Tfailure__result__exists__context, parent:Cexists__context__mutation) => new Cfailure__result__exists__context(init, parent);
				case 'success': return (init:Tsuccess__result__exists__context, parent:Cexists__context__mutation) => new Csuccess__result__exists__context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return finalize_failure__result__exists__context;
				case 'success': return finalize_success__result__exists__context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texists__context__mutation['result'], parent:Cexists__context__mutation) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Cnotification {
	export class Dcontext extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__context__notification, init:Tdoes_not_exist__context__notification}|
		{ name: 'exists', node:Cexists__context__notification, init:Texists__context__notification}|
		{ name: 'permission denied', node:Cpermission_denied__context__notification, init:Tpermission_denied__context__notification}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__context__notification, parent:Cnotification) => new Cdoes_not_exist__context__notification(init, parent);
				case 'exists': return (init:Texists__context__notification, parent:Cnotification) => new Cexists__context__notification(init, parent);
				case 'permission denied': return (init:Tpermission_denied__context__notification, parent:Cnotification) => new Cpermission_denied__context__notification(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__context__notification;
				case 'exists': return finalize_exists__context__notification;
				case 'permission denied': return finalize_permission_denied__context__notification;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnotification['context'], parent:Cnotification) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cexists__context__notification {
	export class Dnotification_data extends Cnotification_data {
		constructor(data:Texists__context__notification['notification data'], parent:Cexists__context__notification) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.source())
					.then(context => context?.properties.node_subscription).result!)
			}, 'notification data')
		}
	}
}
export namespace Cpassword_mutation {
	export class Duser_context extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__user_context__password_mutation, init:Tdoes_not_exist__user_context__password_mutation}|
		{ name: 'exists', node:Cexists__user_context__password_mutation, init:Texists__user_context__password_mutation}|
		{ name: 'permission denied', node:Cpermission_denied__user_context__password_mutation, init:Tpermission_denied__user_context__password_mutation}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__user_context__password_mutation, parent:Cpassword_mutation) => new Cdoes_not_exist__user_context__password_mutation(init, parent);
				case 'exists': return (init:Texists__user_context__password_mutation, parent:Cpassword_mutation) => new Cexists__user_context__password_mutation(init, parent);
				case 'permission denied': return (init:Tpermission_denied__user_context__password_mutation, parent:Cpassword_mutation) => new Cpermission_denied__user_context__password_mutation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__user_context__password_mutation;
				case 'exists': return finalize_exists__user_context__password_mutation;
				case 'permission denied': return finalize_permission_denied__user_context__password_mutation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tpassword_mutation['user context'], parent:Cpassword_mutation) {
			super(data, parent, 'user context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'user context'`; }
	}
}
export namespace Cexists__user_context__password_mutation {
	export class Dresult extends StateGroup<
		{ name: 'failure', node:Cfailure__result__exists__user_context__password_mutation, init:Tfailure__result__exists__user_context__password_mutation}|
		{ name: 'success', node:Csuccess__result__exists__user_context__password_mutation, init:Tsuccess__result__exists__user_context__password_mutation}> {
		protected initializer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return (init:Tfailure__result__exists__user_context__password_mutation, parent:Cexists__user_context__password_mutation) => new Cfailure__result__exists__user_context__password_mutation(init, parent);
				case 'success': return (init:Tsuccess__result__exists__user_context__password_mutation, parent:Cexists__user_context__password_mutation) => new Csuccess__result__exists__user_context__password_mutation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return finalize_failure__result__exists__user_context__password_mutation;
				case 'success': return finalize_success__result__exists__user_context__password_mutation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texists__user_context__password_mutation['result'], parent:Cexists__user_context__password_mutation) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Cpassword_reset {
	export class Duser_context extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__user_context__password_reset, init:Tdoes_not_exist__user_context__password_reset}|
		{ name: 'exists', node:Cexists__user_context__password_reset, init:Texists__user_context__password_reset}|
		{ name: 'permission denied', node:Cpermission_denied__user_context__password_reset, init:Tpermission_denied__user_context__password_reset}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__user_context__password_reset, parent:Cpassword_reset) => new Cdoes_not_exist__user_context__password_reset(init, parent);
				case 'exists': return (init:Texists__user_context__password_reset, parent:Cpassword_reset) => new Cexists__user_context__password_reset(init, parent);
				case 'permission denied': return (init:Tpermission_denied__user_context__password_reset, parent:Cpassword_reset) => new Cpermission_denied__user_context__password_reset(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__user_context__password_reset;
				case 'exists': return finalize_exists__user_context__password_reset;
				case 'permission denied': return finalize_permission_denied__user_context__password_reset;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tpassword_reset['user context'], parent:Cpassword_reset) {
			super(data, parent, 'user context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'user context'`; }
	}
}
export namespace Cexists__user_context__password_reset {
	export class Dresult extends StateGroup<
		{ name: 'failure', node:Cfailure__result__exists__user_context__password_reset, init:Tfailure__result__exists__user_context__password_reset}|
		{ name: 'success', node:Csuccess__result__exists__user_context__password_reset, init:Tsuccess__result__exists__user_context__password_reset}> {
		protected initializer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return (init:Tfailure__result__exists__user_context__password_reset, parent:Cexists__user_context__password_reset) => new Cfailure__result__exists__user_context__password_reset(init, parent);
				case 'success': return (init:Tsuccess__result__exists__user_context__password_reset, parent:Cexists__user_context__password_reset) => new Csuccess__result__exists__user_context__password_reset(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return finalize_failure__result__exists__user_context__password_reset;
				case 'success': return finalize_success__result__exists__user_context__password_reset;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texists__user_context__password_reset['result'], parent:Cexists__user_context__password_reset) {
			super(data, parent, 'result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result'`; }
	}
}
export namespace Csuccess__result__exists__user_context__password_reset {
}
export namespace Csubscription {
	export class Dcontext extends StateGroup<
		{ name: 'does not exist', node:Cdoes_not_exist__context__subscription, init:Tdoes_not_exist__context__subscription}|
		{ name: 'exists', node:Cexists__context__subscription, init:Texists__context__subscription}|
		{ name: 'permission denied', node:Cpermission_denied__context__subscription, init:Tpermission_denied__context__subscription}> {
		protected initializer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return (init:Tdoes_not_exist__context__subscription, parent:Csubscription) => new Cdoes_not_exist__context__subscription(init, parent);
				case 'exists': return (init:Texists__context__subscription, parent:Csubscription) => new Cexists__context__subscription(init, parent);
				case 'permission denied': return (init:Tpermission_denied__context__subscription, parent:Csubscription) => new Cpermission_denied__context__subscription(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'does not exist'|'exists'|'permission denied') {
			switch (state) {
				case 'does not exist': return finalize_does_not_exist__context__subscription;
				case 'exists': return finalize_exists__context__subscription;
				case 'permission denied': return finalize_permission_denied__context__subscription;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsubscription['context'], parent:Csubscription) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cexists__context__subscription {
	export class Ddelete_permitted extends Cpermission_data {
		constructor(data:Texists__context__subscription['delete permitted'], parent:Cexists__context__subscription) {
			super(data, parent, 'delete permitted')
		}
	}
	export class Dsubscription_result_data extends Csubscription_result_data {
		constructor(data:Texists__context__subscription['subscription result data'], parent:Cexists__context__subscription) {
			super(data, parent, {
				node_subscription: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.source())
					.then(context => context?.properties.node_subscription).result!)
			}, 'subscription result data')
		}
	}
}
function finalize_no__has_steps__collection_query_entries(obj:Cno__has_steps__collection_query_entries, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cno__has_steps__collection_query_path>obj.inferences.has_steps)(detach) !== undefined || detach);
	finalize_node_query_result(obj.properties.properties, detach);
}
function finalize_no__reference_result__candidate(obj:Cno__reference_result__candidate, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cno__include_reference__candidates>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_yes__reference_result__candidate(obj:Cyes__reference_result__candidate, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__include_reference__candidates>obj.inferences.yes)(detach) !== undefined || detach);
	finalize_id_path__client_reply(obj.properties.id_path, detach);
}
function finalize_candidate(obj:Ccandidate, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ccandidates>obj.inferences.candidates)(detach) !== undefined || detach);
	switch (obj.properties.reference_result.state.name) {
		case 'no': finalize_no__reference_result__candidate(obj.properties.reference_result.state.node, detach); break;
		case 'yes': finalize_yes__reference_result__candidate(obj.properties.reference_result.state.node, detach); break;
	}
}
function finalize_existing_entry(obj:Cexisting_entry, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cexisting_entries>obj.inferences.existing_entries)(detach) !== undefined || detach);
}
function finalize_entries__yes(obj:Centries__yes, detach:boolean = false) {
	finalize_collection_query_entries(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'candidate': finalize_candidate(obj.properties.type.state.node, detach); break;
		case 'existing entry': finalize_existing_entry(obj.properties.type.state.node, detach); break;
	}
}
function finalize_yes__has_steps__collection_query_entries(obj:Cyes__has_steps__collection_query_entries, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__has_steps__collection_query_path>obj.inferences.step)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.entries) {
		finalize_entries__yes(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_collection_query_entries(obj:Ccollection_query_entries, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ccollection_query_path>obj.input.collection_path)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_request.Cyes__select_entries>obj.input.select_entries)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__collection_query_entries(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__collection_query_entries(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_entries__collection__type__entry_points(obj:Centries__collection__type__entry_points, detach:boolean = false) {
	finalize_entry_points(obj.properties.entry_points, detach);
}
function finalize_collection__type__entry_points(obj:Ccollection__type__entry_points, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__has_steps__entry_point_path>obj.inferences.has_step)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.entries) {
		finalize_entries__collection__type__entry_points(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_flat_graph(obj:Cflat_graph, detach:boolean = false) {
	finalize_list_query_result_entries(obj.properties.entries, detach);
}
function finalize_query(obj:Cquery, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cno__has_steps__entry_point_path>obj.inferences.no_step)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.flat_graph) {
		finalize_flat_graph(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_entry_points(obj:Centry_points, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.input.application)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_request.Centry_point_path>obj.input.entry_path)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_request.Cquery>obj.input.list_query)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__entry_points(obj.properties.type.state.node, detach); break;
		case 'query': finalize_query(obj.properties.type.state.node, detach); break;
	}
}
function finalize_children(obj:Cchildren, detach:boolean = false) {
	finalize_graph_node(obj.properties.graph, detach);
}
function finalize_graph_node(obj:Cgraph_node, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.children) {
		finalize_children(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_no__has_steps__id_path(obj:Cno__has_steps__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_collection_entry(obj:Ccollection_entry, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
}
function finalize_group__type__yes(obj:Cgroup__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state(obj:Cstate, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__id_path(obj:Cyes__has_steps__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	finalize_id_path__client_reply(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'collection entry': finalize_collection_entry(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__yes(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state(obj.properties.type.state.node, detach); break;
	}
}
function finalize_id_path__client_reply(obj:Cid_path__client_reply, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__id_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__id_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_entries__collection__type__list_query_result_entries(obj:Centries__collection__type__list_query_result_entries, detach:boolean = false) {
	finalize_list_query_result_entries(obj.properties.result, detach);
}
function finalize_collection__type__list_query_result_entries(obj:Ccollection__type__list_query_result_entries, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__has_steps__collection_path>obj.inferences.has_steps)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.entries) {
		finalize_entries__collection__type__list_query_result_entries(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_no__is_set(obj:Cno__is_set, detach:boolean = false) {
}
function finalize_number__type__yes(obj:Cnumber__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cnumber__type__properties__query>obj.inferences.number)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.properties.value.inferences.set_type)(detach) !== undefined || detach);
}
function finalize_state_group__type__yes(obj:Cstate_group__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cstate_group__type__properties__query>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__yes(obj:Ctext__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ctext__type__properties__query>obj.inferences.text)(detach) !== undefined || detach);
}
function finalize_yes__is_set(obj:Cyes__is_set, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__yes(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__yes(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__yes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_properties__node(obj:Cproperties__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cproperties__query>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.is_set.state.name) {
		case 'no': finalize_no__is_set(obj.properties.is_set.state.node, detach); break;
		case 'yes': finalize_yes__is_set(obj.properties.is_set.state.node, detach); break;
	}
}
function finalize_node__type(obj:Cnode__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cno__has_steps__collection_path>obj.inferences.has_steps)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__node(entry, detach);
	}
	if (!detach) {
		(obj.properties.properties as any).eval_required_keys(detach);
	}
}
function finalize_none__type(obj:Cnone__type, detach:boolean = false) {
}
function finalize_list_query_result_entries(obj:Clist_query_result_entries, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.input.application)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_request.Ccollection_path>obj.input.collection_path)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_request.Cquery>obj.input.list_query)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__list_query_result_entries(obj.properties.type.state.node, detach); break;
		case 'node': finalize_node__type(obj.properties.type.state.node, detach); break;
		case 'none': finalize_none__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_does_not_exist__result(obj:Cdoes_not_exist__result, detach:boolean = false) {
}
function finalize_entries__collection__type__exists(obj:Centries__collection__type__exists, detach:boolean = false) {
	finalize_node_query_result(obj.properties.properties, detach);
}
function finalize_no__has_graph_endpoints(obj:Cno__has_graph_endpoints, detach:boolean = false) {
}
function finalize_ordered_graphs(obj:Cordered_graphs, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cordered>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_graph_endpoints(obj:Cyes__has_graph_endpoints, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__include_graph_endpoints__collection__type__properties>obj.inferences.include_endpoints)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.ordered_graphs) {
		finalize_ordered_graphs(entry, detach);
	}
	if (!detach) {
		(obj.properties.ordered_graphs as any).eval_required_keys(detach);
	}
}
function finalize_collection__type__exists(obj:Ccollection__type__exists, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ccollection__type__properties__node_query>obj.inferences.collection)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.entries) {
		finalize_entries__collection__type__exists(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.has_graph_endpoints.state.name) {
		case 'no': finalize_no__has_graph_endpoints(obj.properties.has_graph_endpoints.state.node, detach); break;
		case 'yes': finalize_yes__has_graph_endpoints(obj.properties.has_graph_endpoints.state.node, detach); break;
	}
}
function finalize_file__type__exists(obj:Cfile__type__exists, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cfile__type__properties__node_query>obj.inferences.file)(detach) !== undefined || detach);
}
function finalize_id_path__type(obj:Cid_path__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cid_path__type>obj.inferences.id_path)(detach) !== undefined || detach);
	finalize_id_path__client_reply(obj.properties.id_path, detach);
}
function finalize_number__type__exists(obj:Cnumber__type__exists, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cnumber__type__properties__node_query>obj.inferences.number)(detach) !== undefined || detach);
}
function finalize_state_group__type__exists(obj:Cstate_group__type__exists, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cstate_group__type__properties__node_query>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__exists(obj:Ctext__type__exists, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ctext__type__properties__node_query>obj.inferences.text)(detach) !== undefined || detach);
}
function finalize_exists__result(obj:Cexists__result, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__exists(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__exists(obj.properties.type.state.node, detach); break;
		case 'id path': finalize_id_path__type(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__exists(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__exists(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__exists(obj.properties.type.state.node, detach); break;
	}
}
function finalize_permission_denied__result__properties(obj:Cpermission_denied__result__properties, detach:boolean = false) {
}
function finalize_properties__node_query_result(obj:Cproperties__node_query_result, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cproperties__node_query>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.result.state.name) {
		case 'does not exist': finalize_does_not_exist__result(obj.properties.result.state.node, detach); break;
		case 'exists': finalize_exists__result(obj.properties.result.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__result__properties(obj.properties.result.state.node, detach); break;
	}
}
function finalize_node_query_result(obj:Cnode_query_result, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cnode_query>obj.input.node_query)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__node_query_result(entry, detach);
	}
	if (!detach) {
		(obj.properties.properties as any).eval_required_keys(detach);
	}
}
function finalize_no__graph_endpoints_update(obj:Cno__graph_endpoints_update, detach:boolean = false) {
}
function finalize_graph_endpoints__endpoints__result__yes__graph_endpoints_update(obj:Cgraph_endpoints__endpoints__result__yes__graph_endpoints_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cordered>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_endpoints__result__yes__graph_endpoints_update(obj:Cendpoints__result__yes__graph_endpoints_update, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.graph_endpoints) {
		finalize_graph_endpoints__endpoints__result__yes__graph_endpoints_update(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_none__result__yes__graph_endpoints_update(obj:Cnone__result__yes__graph_endpoints_update, detach:boolean = false) {
}
function finalize_yes__graph_endpoints_update(obj:Cyes__graph_endpoints_update, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__include_graph_endpoints__collection__type__property>obj.inferences.yes)(detach) !== undefined || detach);
	switch (obj.properties.result.state.name) {
		case 'endpoints': finalize_endpoints__result__yes__graph_endpoints_update(obj.properties.result.state.node, detach); break;
		case 'none': finalize_none__result__yes__graph_endpoints_update(obj.properties.result.state.node, detach); break;
	}
}
function finalize_create(obj:Ccreate, detach:boolean = false) {
	finalize_permission_data(obj.properties.delete_permitted, detach);
	finalize_subscription_result_data(obj.properties.node_data, detach);
}
function finalize_remove(obj:Cremove, detach:boolean = false) {
}
function finalize_update__type__entries(obj:Cupdate__type__entries, detach:boolean = false) {
	finalize_notification_data(obj.properties.node_data, detach);
}
function finalize_entries__entries__value_update(obj:Centries__entries__value_update, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'create': finalize_create(obj.properties.type.state.node, detach); break;
		case 'remove': finalize_remove(obj.properties.type.state.node, detach); break;
		case 'update': finalize_update__type__entries(obj.properties.type.state.node, detach); break;
	}
}
function finalize_entries__value_update(obj:Centries__value_update, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Centries__subscribe_on>obj.inferences.subscribe_on_entries)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.entries) {
		finalize_entries__entries__value_update(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_no__value_update__collection(obj:Cno__value_update__collection, detach:boolean = false) {
}
function finalize_updated(obj:Cupdated, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cupdates>obj.inferences.updates)(detach) !== undefined || detach);
}
function finalize_collection__type__properties(obj:Ccollection__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ccollection__type__property__type__attributes>obj.inferences.collection)(detach) !== undefined || detach);
	switch (obj.properties.graph_endpoints_update.state.name) {
		case 'no': finalize_no__graph_endpoints_update(obj.properties.graph_endpoints_update.state.node, detach); break;
		case 'yes': finalize_yes__graph_endpoints_update(obj.properties.graph_endpoints_update.state.node, detach); break;
	}
	switch (obj.properties.value_update.state.name) {
		case 'entries': finalize_entries__value_update(obj.properties.value_update.state.node, detach); break;
		case 'no': finalize_no__value_update__collection(obj.properties.value_update.state.node, detach); break;
		case 'updated': finalize_updated(obj.properties.value_update.state.node, detach); break;
	}
}
function finalize_file__type__properties(obj:Cfile__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cfile__type__property>obj.inferences.file)(detach) !== undefined || detach);
}
function finalize_group__type__properties(obj:Cgroup__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	finalize_notification_data(obj.properties.notification_data, detach);
}
function finalize_number__type__properties(obj:Cnumber__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cnumber__type__property__type__attributes>obj.inferences.number)(detach) !== undefined || detach);
}
function finalize_set(obj:Cset, detach:boolean = false) {
	finalize_permission_data(obj.properties.delete_permitted, detach);
	finalize_subscription_result_data(obj.properties.initialization_data, detach);
}
function finalize_update__type__state_group(obj:Cupdate__type__state_group, detach:boolean = false) {
	finalize_notification_data(obj.properties.notification_data, detach);
}
function finalize_state_group__type__properties(obj:Cstate_group__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cstate_group__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_request.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'set': finalize_set(obj.properties.type.state.node, detach); break;
		case 'update': finalize_update__type__state_group(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__reference_update(obj:Cno__reference_update, detach:boolean = false) {
}
function finalize_disconnected__result__yes__reference_update(obj:Cdisconnected__result__yes__reference_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__type>obj.inferences.optional_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.inferences.reference)(detach) !== undefined || detach);
}
function finalize_node__result__yes__reference_update(obj:Cnode__result__yes__reference_update, detach:boolean = false) {
	finalize_id_path__client_reply(obj.properties.id_path, detach);
}
function finalize_permission_denied__result__yes__reference_update(obj:Cpermission_denied__result__yes__reference_update, detach:boolean = false) {
}
function finalize_yes__reference_update(obj:Cyes__reference_update, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__include_reference__text>obj.inferences.yes)(detach) !== undefined || detach);
	switch (obj.properties.result.state.name) {
		case 'disconnected': finalize_disconnected__result__yes__reference_update(obj.properties.result.state.node, detach); break;
		case 'node': finalize_node__result__yes__reference_update(obj.properties.result.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__result__yes__reference_update(obj.properties.result.state.node, detach); break;
	}
}
function finalize_no__value_update__text(obj:Cno__value_update__text, detach:boolean = false) {
}
function finalize_yes__value_update(obj:Cyes__value_update, detach:boolean = false) {
}
function finalize_text__type__properties(obj:Ctext__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ctext__type__property__type__attributes>obj.inferences.text)(detach) !== undefined || detach);
	switch (obj.properties.reference_update.state.name) {
		case 'no': finalize_no__reference_update(obj.properties.reference_update.state.node, detach); break;
		case 'yes': finalize_yes__reference_update(obj.properties.reference_update.state.node, detach); break;
	}
	switch (obj.properties.value_update.state.name) {
		case 'no': finalize_no__value_update__text(obj.properties.value_update.state.node, detach); break;
		case 'yes': finalize_yes__value_update(obj.properties.value_update.state.node, detach); break;
	}
}
function finalize_properties__notification_data(obj:Cproperties__notification_data, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cproperty__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__properties(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__properties(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__properties(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__properties(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__properties(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties(obj.properties.type.state.node, detach); break;
	}
}
function finalize_notification_data(obj:Cnotification_data, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cnode_subscription>obj.input.node_subscription)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__notification_data(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_no__permitted(obj:Cno__permitted, detach:boolean = false) {
}
function finalize_yes__permitted(obj:Cyes__permitted, detach:boolean = false) {
}
function finalize_permission_data(obj:Cpermission_data, detach:boolean = false) {
	switch (obj.properties.permitted.state.name) {
		case 'no': finalize_no__permitted(obj.properties.permitted.state.node, detach); break;
		case 'yes': finalize_yes__permitted(obj.properties.permitted.state.node, detach); break;
	}
}
function finalize_no__read_permitted(obj:Cno__read_permitted, detach:boolean = false) {
}
function finalize_command(obj:Ccommand, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ccommand>obj.inferences.command)(detach) !== undefined || detach);
	finalize_permission_data(obj.properties.execute_permitted, detach);
}
function finalize_no__graph_endpoints_result(obj:Cno__graph_endpoints_result, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cno__include_graph_endpoints__collection__type__property>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_graph_endpoints__endpoints__result__yes__graph_endpoints_result(obj:Cgraph_endpoints__endpoints__result__yes__graph_endpoints_result, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cordered>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_endpoints__result__yes__graph_endpoints_result(obj:Cendpoints__result__yes__graph_endpoints_result, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.graph_endpoints) {
		finalize_graph_endpoints__endpoints__result__yes__graph_endpoints_result(entry, detach);
	}
	if (!detach) {
		(obj.properties.graph_endpoints as any).eval_required_keys(detach);
	}
}
function finalize_none__result__yes__graph_endpoints_result(obj:Cnone__result__yes__graph_endpoints_result, detach:boolean = false) {
}
function finalize_yes__graph_endpoints_result(obj:Cyes__graph_endpoints_result, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__include_graph_endpoints__collection__type__property>obj.inferences.yes)(detach) !== undefined || detach);
	switch (obj.properties.result.state.name) {
		case 'endpoints': finalize_endpoints__result__yes__graph_endpoints_result(obj.properties.result.state.node, detach); break;
		case 'none': finalize_none__result__yes__graph_endpoints_result(obj.properties.result.state.node, detach); break;
	}
}
function finalize_entries__entries__result(obj:Centries__entries__result, detach:boolean = false) {
	finalize_permission_data(obj.properties.delete_permitted, detach);
	finalize_subscription_result_data(obj.properties.node_data, detach);
}
function finalize_entries__result(obj:Centries__result, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Centries__subscribe_on>obj.inferences.subscribe_on_entries)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.entries) {
		finalize_entries__entries__result(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_update__result(obj:Cupdate__result, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cupdates>obj.inferences.update)(detach) !== undefined || detach);
}
function finalize_collection__type__property(obj:Ccollection__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ccollection__type__property__type__attributes>obj.inferences.collection)(detach) !== undefined || detach);
	finalize_permission_data(obj.properties.create_permitted, detach);
	switch (obj.properties.graph_endpoints_result.state.name) {
		case 'no': finalize_no__graph_endpoints_result(obj.properties.graph_endpoints_result.state.node, detach); break;
		case 'yes': finalize_yes__graph_endpoints_result(obj.properties.graph_endpoints_result.state.node, detach); break;
	}
	switch (obj.properties.result.state.name) {
		case 'entries': finalize_entries__result(obj.properties.result.state.node, detach); break;
		case 'update': finalize_update__result(obj.properties.result.state.node, detach); break;
	}
}
function finalize_file__type__property(obj:Cfile__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cfile__type__property>obj.inferences.file)(detach) !== undefined || detach);
}
function finalize_group__type__property(obj:Cgroup__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	finalize_subscription_result_data(obj.properties.node_data, detach);
}
function finalize_number__type__property(obj:Cnumber__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cnumber__type__property__type__attributes>obj.inferences.number)(detach) !== undefined || detach);
}
function finalize_create_permitted(obj:Ccreate_permitted, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_group__type__property(obj:Cstate_group__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cstate_group__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.create_permitted) {
		finalize_create_permitted(entry, detach);
	}
	if (!detach) {
	}
	finalize_permission_data(obj.properties.delete_permitted, detach);
	finalize_subscription_result_data(obj.properties.node_data, detach);
	assert((<(detach?:boolean) => client_request.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__reference_result__text(obj:Cno__reference_result__text, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cno__include_reference__text>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_disconnected__result__yes__reference_result(obj:Cdisconnected__result__yes__reference_result, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__type>obj.inferences.optional_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.inferences.reference)(detach) !== undefined || detach);
}
function finalize_node__result__yes__reference_result(obj:Cnode__result__yes__reference_result, detach:boolean = false) {
	finalize_id_path__client_reply(obj.properties.id_path, detach);
}
function finalize_permission_denied__result__yes__reference_result(obj:Cpermission_denied__result__yes__reference_result, detach:boolean = false) {
}
function finalize_yes__reference_result__text(obj:Cyes__reference_result__text, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__include_reference__text>obj.inferences.yes)(detach) !== undefined || detach);
	switch (obj.properties.result.state.name) {
		case 'disconnected': finalize_disconnected__result__yes__reference_result(obj.properties.result.state.node, detach); break;
		case 'node': finalize_node__result__yes__reference_result(obj.properties.result.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__result__yes__reference_result(obj.properties.result.state.node, detach); break;
	}
}
function finalize_text__type__property(obj:Ctext__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ctext__type__property__type__attributes>obj.inferences.text)(detach) !== undefined || detach);
	switch (obj.properties.reference_result.state.name) {
		case 'no': finalize_no__reference_result__text(obj.properties.reference_result.state.node, detach); break;
		case 'yes': finalize_yes__reference_result__text(obj.properties.reference_result.state.node, detach); break;
	}
}
function finalize_property(obj:Cproperty, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cproperty__type__attributes>obj.inferences.property)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__property(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__property(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__property(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__property(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__property(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_attributes(obj:Cattributes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cattributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'command': finalize_command(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_yes__read_permitted(obj:Cyes__read_permitted, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.attributes) {
		finalize_attributes(entry, detach);
	}
	if (!detach) {
		(obj.properties.attributes as any).eval_required_keys(detach);
	}
}
function finalize_subscription_result_data(obj:Csubscription_result_data, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cnode_subscription>obj.input.node_subscription)(detach) !== undefined || detach);
	switch (obj.properties.read_permitted.state.name) {
		case 'no': finalize_no__read_permitted(obj.properties.read_permitted.state.node, detach); break;
		case 'yes': finalize_yes__read_permitted(obj.properties.read_permitted.state.node, detach); break;
	}
	finalize_permission_data(obj.properties.update_permitted, detach);
}
function finalize_does_not_exist__context__acyclic_graph_list_query(obj:Cdoes_not_exist__context__acyclic_graph_list_query, detach:boolean = false) {
}
function finalize_exists__context__acyclic_graph_list_query(obj:Cexists__context__acyclic_graph_list_query, detach:boolean = false) {
	finalize_entry_points(obj.properties.entry_points, detach);
}
function finalize_permission_denied__context__acyclic_graph_list_query(obj:Cpermission_denied__context__acyclic_graph_list_query, detach:boolean = false) {
}
function finalize_acyclic_graph_list_query(obj:Cacyclic_graph_list_query, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cacyclic_graph_list_query>obj.inferences.source)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'does not exist': finalize_does_not_exist__context__acyclic_graph_list_query(obj.properties.context.state.node, detach); break;
		case 'exists': finalize_exists__context__acyclic_graph_list_query(obj.properties.context.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__context__acyclic_graph_list_query(obj.properties.context.state.node, detach); break;
	}
}
function finalize_does_not_exist__context__acyclic_graph_tree_query(obj:Cdoes_not_exist__context__acyclic_graph_tree_query, detach:boolean = false) {
}
function finalize_exists__context__acyclic_graph_tree_query(obj:Cexists__context__acyclic_graph_tree_query, detach:boolean = false) {
	finalize_graph_node(obj.properties.graph, detach);
}
function finalize_permission_denied__context__acyclic_graph_tree_query(obj:Cpermission_denied__context__acyclic_graph_tree_query, detach:boolean = false) {
}
function finalize_acyclic_graph_tree_query(obj:Cacyclic_graph_tree_query, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cacyclic_graph_tree_query>obj.inferences.source)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'does not exist': finalize_does_not_exist__context__acyclic_graph_tree_query(obj.properties.context.state.node, detach); break;
		case 'exists': finalize_exists__context__acyclic_graph_tree_query(obj.properties.context.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__context__acyclic_graph_tree_query(obj.properties.context.state.node, detach); break;
	}
}
function finalize_does_not_exist__context__collection_query(obj:Cdoes_not_exist__context__collection_query, detach:boolean = false) {
}
function finalize_states(obj:Cstates, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_distribution(obj:Cstate_distribution, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cstate_distribution>obj.inferences.state_distribution_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_aggregates(obj:Caggregates, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Caggregates>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'state distribution': finalize_state_distribution(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__entries(obj:Cno__entries, detach:boolean = false) {
}
function finalize_no__limit_exceeded(obj:Cno__limit_exceeded, detach:boolean = false) {
}
function finalize_yes__limit_exceeded(obj:Cyes__limit_exceeded, detach:boolean = false) {
}
function finalize_yes__entries(obj:Cyes__entries, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__select_entries>obj.inferences.select_entries)(detach) !== undefined || detach);
	finalize_collection_query_entries(obj.properties.entries, detach);
	switch (obj.properties.limit_exceeded.state.name) {
		case 'no': finalize_no__limit_exceeded(obj.properties.limit_exceeded.state.node, detach); break;
		case 'yes': finalize_yes__limit_exceeded(obj.properties.limit_exceeded.state.node, detach); break;
	}
}
function finalize_exists__context__collection_query(obj:Cexists__context__collection_query, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.aggregates) {
		finalize_aggregates(entry, detach);
	}
	if (!detach) {
		(obj.properties.aggregates as any).eval_required_keys(detach);
	}
	switch (obj.properties.entries.state.name) {
		case 'no': finalize_no__entries(obj.properties.entries.state.node, detach); break;
		case 'yes': finalize_yes__entries(obj.properties.entries.state.node, detach); break;
	}
}
function finalize_permission_denied__context__collection_query(obj:Cpermission_denied__context__collection_query, detach:boolean = false) {
}
function finalize_collection_query(obj:Ccollection_query, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ccollection_query>obj.inferences.source)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'does not exist': finalize_does_not_exist__context__collection_query(obj.properties.context.state.node, detach); break;
		case 'exists': finalize_exists__context__collection_query(obj.properties.context.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__context__collection_query(obj.properties.context.state.node, detach); break;
	}
}
function finalize_command_execution(obj:Ccommand_execution, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Ccommand_execution>obj.inferences.source)(detach) !== undefined || detach);
}
function finalize_does_not_exist__context__mutation(obj:Cdoes_not_exist__context__mutation, detach:boolean = false) {
}
function finalize_failure__result__exists__context(obj:Cfailure__result__exists__context, detach:boolean = false) {
}
function finalize_success__result__exists__context(obj:Csuccess__result__exists__context, detach:boolean = false) {
}
function finalize_exists__context__mutation(obj:Cexists__context__mutation, detach:boolean = false) {
	switch (obj.properties.result.state.name) {
		case 'failure': finalize_failure__result__exists__context(obj.properties.result.state.node, detach); break;
		case 'success': finalize_success__result__exists__context(obj.properties.result.state.node, detach); break;
	}
}
function finalize_permission_denied__context__mutation(obj:Cpermission_denied__context__mutation, detach:boolean = false) {
}
function finalize_mutation(obj:Cmutation, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cmutation>obj.inferences.source)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'does not exist': finalize_does_not_exist__context__mutation(obj.properties.context.state.node, detach); break;
		case 'exists': finalize_exists__context__mutation(obj.properties.context.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__context__mutation(obj.properties.context.state.node, detach); break;
	}
}
function finalize_does_not_exist__context__notification(obj:Cdoes_not_exist__context__notification, detach:boolean = false) {
}
function finalize_exists__context__notification(obj:Cexists__context__notification, detach:boolean = false) {
	finalize_notification_data(obj.properties.notification_data, detach);
}
function finalize_permission_denied__context__notification(obj:Cpermission_denied__context__notification, detach:boolean = false) {
}
function finalize_notification(obj:Cnotification, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Csubscription>obj.inferences.source)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'does not exist': finalize_does_not_exist__context__notification(obj.properties.context.state.node, detach); break;
		case 'exists': finalize_exists__context__notification(obj.properties.context.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__context__notification(obj.properties.context.state.node, detach); break;
	}
}
function finalize_does_not_exist__user_context__password_mutation(obj:Cdoes_not_exist__user_context__password_mutation, detach:boolean = false) {
}
function finalize_failure__result__exists__user_context__password_mutation(obj:Cfailure__result__exists__user_context__password_mutation, detach:boolean = false) {
}
function finalize_success__result__exists__user_context__password_mutation(obj:Csuccess__result__exists__user_context__password_mutation, detach:boolean = false) {
}
function finalize_exists__user_context__password_mutation(obj:Cexists__user_context__password_mutation, detach:boolean = false) {
	switch (obj.properties.result.state.name) {
		case 'failure': finalize_failure__result__exists__user_context__password_mutation(obj.properties.result.state.node, detach); break;
		case 'success': finalize_success__result__exists__user_context__password_mutation(obj.properties.result.state.node, detach); break;
	}
}
function finalize_permission_denied__user_context__password_mutation(obj:Cpermission_denied__user_context__password_mutation, detach:boolean = false) {
}
function finalize_password_mutation(obj:Cpassword_mutation, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cpassword_mutation>obj.inferences.source)(detach) !== undefined || detach);
	switch (obj.properties.user_context.state.name) {
		case 'does not exist': finalize_does_not_exist__user_context__password_mutation(obj.properties.user_context.state.node, detach); break;
		case 'exists': finalize_exists__user_context__password_mutation(obj.properties.user_context.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__user_context__password_mutation(obj.properties.user_context.state.node, detach); break;
	}
}
function finalize_does_not_exist__user_context__password_reset(obj:Cdoes_not_exist__user_context__password_reset, detach:boolean = false) {
}
function finalize_failure__result__exists__user_context__password_reset(obj:Cfailure__result__exists__user_context__password_reset, detach:boolean = false) {
}
function finalize_success__result__exists__user_context__password_reset(obj:Csuccess__result__exists__user_context__password_reset, detach:boolean = false) {
}
function finalize_exists__user_context__password_reset(obj:Cexists__user_context__password_reset, detach:boolean = false) {
	switch (obj.properties.result.state.name) {
		case 'failure': finalize_failure__result__exists__user_context__password_reset(obj.properties.result.state.node, detach); break;
		case 'success': finalize_success__result__exists__user_context__password_reset(obj.properties.result.state.node, detach); break;
	}
}
function finalize_permission_denied__user_context__password_reset(obj:Cpermission_denied__user_context__password_reset, detach:boolean = false) {
}
function finalize_password_reset(obj:Cpassword_reset, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cpassword_reset>obj.inferences.source)(detach) !== undefined || detach);
	switch (obj.properties.user_context.state.name) {
		case 'does not exist': finalize_does_not_exist__user_context__password_reset(obj.properties.user_context.state.node, detach); break;
		case 'exists': finalize_exists__user_context__password_reset(obj.properties.user_context.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__user_context__password_reset(obj.properties.user_context.state.node, detach); break;
	}
}
function finalize_does_not_exist__context__subscription(obj:Cdoes_not_exist__context__subscription, detach:boolean = false) {
}
function finalize_exists__context__subscription(obj:Cexists__context__subscription, detach:boolean = false) {
	finalize_permission_data(obj.properties.delete_permitted, detach);
	finalize_subscription_result_data(obj.properties.subscription_result_data, detach);
}
function finalize_permission_denied__context__subscription(obj:Cpermission_denied__context__subscription, detach:boolean = false) {
}
function finalize_subscription(obj:Csubscription, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Csubscription>obj.inferences.source)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'does not exist': finalize_does_not_exist__context__subscription(obj.properties.context.state.node, detach); break;
		case 'exists': finalize_exists__context__subscription(obj.properties.context.state.node, detach); break;
		case 'permission denied': finalize_permission_denied__context__subscription(obj.properties.context.state.node, detach); break;
	}
}
function finalize_client_reply(obj:Cclient_reply, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'acyclic graph list query': finalize_acyclic_graph_list_query(obj.properties.type.state.node, detach); break;
		case 'acyclic graph tree query': finalize_acyclic_graph_tree_query(obj.properties.type.state.node, detach); break;
		case 'collection query': finalize_collection_query(obj.properties.type.state.node, detach); break;
		case 'command execution': finalize_command_execution(obj.properties.type.state.node, detach); break;
		case 'mutation': finalize_mutation(obj.properties.type.state.node, detach); break;
		case 'notification': finalize_notification(obj.properties.type.state.node, detach); break;
		case 'password mutation': finalize_password_mutation(obj.properties.type.state.node, detach); break;
		case 'password reset': finalize_password_reset(obj.properties.type.state.node, detach); break;
		case 'subscription': finalize_subscription(obj.properties.type.state.node, detach); break;
	}
}

export namespace Cclient_reply {
	export function create(init:Tclient_reply, input: {
	'application':application.Capplication
	'request':client_request.Cclient_request}, symbols_file?: SymbolsFile):Cclient_reply {
		const instance = new Cclient_reply(init, input as any, symbols_file);
		return instance;
	};
}
